/**
 * GamePlanTab.jsx  v2
 * ────────────────────
 * Week-based game planning calendar.
 * – Training / Match / Competition types
 * – Sport-specific result scoring (tennis sets, football goals, endurance metrics, …)
 * – Plan adherence & performance ratings
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import supabase from '../supabaseClient';
import { fetchAllPlans } from '../utils/plans';
import { netPlayLabel, netPlayIcon } from '../utils/sportLabels';
import { formatDate as formatDateLocaleAware } from '../utils/dateLocal';
import { onEnterOrSpace, useEscapeToClose } from '../utils/a11y';
import { supportsDoubles, opponentLabel, partnerLabel, formatBadge } from '../utils/matchFormat';
import { useSportFiltered } from '../utils/sportScope';
import EquipmentStatModule, { getDefaultEquipmentSnapshot, racketLabel } from './EquipmentManager';
import LayerSection from './LayerSection';
import CompetitionsSplitTable from './CompetitionsSplitTable';
import { confirmDialog } from './ConfirmDialog';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Design ────────────────────────────────────────────────────────────────────
const C = {
  bg:      '#0d1117',
  card:    'rgba(13,17,23,0.92)',
  // Brightened per user feedback — the previous 0.10 alpha made
  // unselected buttons/inputs read as disabled rather than interactive.
  border:  'rgba(255,255,255,0.22)',
  accent:  '#10b981',
  blue:    '#3b82f6',
  // The ZenAnalytics wordmark's light blue (see App.js header, #38bdf8) —
  // used for module card borders/dividers so they read clearly against the
  // dark background instead of the near-invisible translucent white border.
  logoBlue:      '#38bdf8',
  moduleBorder:  'rgba(56,189,248,0.65)',
  amber:   '#f59e0b',
  red:     '#ef4444',
  purple:  '#a78bfa',
  text:    '#e6edf3',
  // Brightened from #8b949e — same feedback: labels/unselected text read
  // too close to the background to tell interactive elements apart.
  muted:   '#a8b3bd',
  dim:     'rgba(255,255,255,0.45)',
  font:    "'Inter', system-ui, sans-serif",
};

// ── Sport library ────────────────────────────────

// ZenAnalytics is a racket-sport match analytics library — the sport picker
// is scoped to that. Legacy Game Plans logged in sports no longer offered
// here (old team-sport data, etc.) keep rendering fine via SPORT_SCORING /
// sportEmoji fallbacks below; nothing about existing data is removed.
const SPORTS = [
  { value: 'tennis',          label: '🎾 Tennis',             emoji: '🎾', group: 'Racket & Net' },
  { value: 'padel',           label: '🏸 Padel',              emoji: '🏸', group: 'Racket & Net' },
  { value: 'squash',          label: '🟡 Squash',             emoji: '🟡', group: 'Racket & Net' },
  { value: 'badminton',       label: '🏸 Badminton',          emoji: '🏸', group: 'Racket & Net' },
  { value: 'pickleball',      label: '🎾 Pickleball',         emoji: '🎾', group: 'Racket & Net' },
  { value: 'table_tennis',    label: '🏓 Table Tennis',       emoji: '🏓', group: 'Racket & Net' },
];

/**
 * Sport scoring definitions — all 6 supported sports are scored as sets.
 *
 * Pruned 2026-10-22 (docs_zenanalytics/18-SPORT-AS-FIRST-CLASS-CONCEPT-2026-10-22.md):
 * this used to carry ~56 more entries (team sports, endurance, combat,
 * martial arts, golf, …) from before the product narrowed to racket
 * sports only. They were kept for a while as a rendering fallback for
 * pre-pivot legacy session_plans rows, until that legacy data itself was
 * deleted the same day — at which point they became genuinely unreachable
 * and were removed, along with the GoalsEntry/EnduranceEntry/
 * TriathlonEntry/GolfEntry components and the SportResultEntry.jsx-shared
 * CombatEntry/MartialArtsEntry/FencingEntry/JudgeScoreEntry/ClimbingEntry/
 * StrengthEntry/CrossFitEntry/PrecisionEntry/EquestrianEntry imports that
 * only those entries ever rendered. `squash` was `type: 'games'` here
 * (a separate, functionally-identical branch from `'sets'` — both just
 * rendered this file's own `<SetsEntry>`); unified to `'sets'` like every
 * other sport, removing the redundant branch.
 */
const SPORT_SCORING = {
  tennis:           { type: 'sets', maxSets: 3,  tiebreak: true, label: 'Sets (6-4 format)' },
  padel:            { type: 'sets', maxSets: 3,  tiebreak: true, label: 'Sets + super tiebreak' },
  squash:           { type: 'sets', maxSets: 5,  setPoints: 11,  label: 'Games to 11' },
  badminton:        { type: 'sets', maxSets: 3,  setPoints: 21,  label: 'Sets to 21' },
  pickleball:       { type: 'sets', maxSets: 3,  setPoints: 11,  label: 'Sets to 11' },
  table_tennis:     { type: 'sets', maxSets: 7,  setPoints: 11,  label: 'Sets to 11' },
};

function sportEmoji(val) { return SPORTS.find(s => s.value === val)?.emoji || '📊'; }
function sportLabel(val) { return SPORTS.find(s => s.value === val)?.label || val || '📊'; }

function getSportType(sport) { return SPORT_SCORING[sport]?.type || 'none'; }
function isTeamSport(sport) { return !!(SPORT_SCORING[sport]?.teamSport); }

// ── Intensity labels (1-5) ────────
const INTENSITY_LABELS = {
  1: { label: 'Recovery',      desc: 'Very light — active recovery / easy',     color: '#22c55e' },
  2: { label: 'Aerobic base',  desc: 'Low — fat burning / Zone 2',              color: '#84cc16' },
  3: { label: 'Aerobic',       desc: 'Moderate — aerobic base building',        color: '#f59e0b' },
  4: { label: 'Anaerobic',     desc: 'Hard — anaerobic / threshold work',       color: '#f97316' },
  5: { label: 'Max effort',    desc: 'All-out — VO2max / sprint intervals',     color: '#ef4444' },
};

// ── Date helpers ──────────────────────────────────────────────────────────────
function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }
function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
function fmtDate(iso) {
  if (!iso) return '—';
  return formatDateLocaleAware(iso);
}

// ── Mobile detection hook ─────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── Sport-specific result entry ───────────────────────────────────────────────

function SetsEntry({ sport, value = {}, onChange }) {
  const cfg = SPORT_SCORING[sport] || SPORT_SCORING.tennis;
  const maxSets = cfg.maxSets || 3;
  const sets = value.sets || Array.from({ length: maxSets }, () => ({ me: '', opp: '' }));

  const updateSet = (i, side, v) => {
    const next = sets.map((s, idx) => idx === i ? { ...s, [side]: v } : s);
    onChange({ ...value, sets: next });
  };

  const inp = {
    width: 36, textAlign: 'center', background: 'rgba(255,255,255,0.09)',
    border: `1px solid ${C.border}`, borderRadius: 6,
    color: C.text, fontSize: 13, padding: '4px 0', fontFamily: C.font,
  };

  return (
    <div>
      <div style={{ color: C.muted, fontSize: 11, marginBottom: 8 }}>
        {cfg.label} — enter games won per set (you / opponent)
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {sets.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: C.muted, fontSize: 10 }}>S{i+1}</span>
            <input type="number" min="0" max="99" value={s.me} placeholder="0"
              onChange={e => updateSet(i, 'me', e.target.value)} style={inp} />
            <span style={{ color: C.dim }}>-</span>
            <input type="number" min="0" max="99" value={s.opp} placeholder="0"
              onChange={e => updateSet(i, 'opp', e.target.value)} style={inp} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Star rating ───────────────────────────────────────────────────────────────
function StarRating({ value, onChange, max = 5 }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {Array.from({ length: max }, (_, i) => i + 1).map(n => {
        const active = n <= (hover || value);
        return (
          <button key={n}
            onClick={() => onChange(value === n ? 0 : n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 22, padding: '0 1px', lineHeight: 1,
              // Unfilled stars used to blend into the dark background
              // (rgba(255,255,255,0.10) — basically invisible), which hid
              // that the field was even interactive. A visible outline
              // star at rest, amber + glow on hover/selected, makes the
              // "tap to rate" affordance obvious against a dark card.
              color: active ? '#f59e0b' : 'rgba(255,255,255,0.38)',
              textShadow: active ? '0 0 8px rgba(245,158,11,0.55)' : 'none',
              transform: hover === n ? 'scale(1.15)' : 'scale(1)',
              transition: 'color 0.1s, transform 0.1s, text-shadow 0.1s',
            }}>{active ? '★' : '☆'}</button>
        );
      })}
    </div>
  );
}


// ── Dictation — per-field voice-to-text for the free-text Game Plan fields.
// One recording per field (not one ramble auto-split across several) since
// routing speech to the right field is an unreliable classification task —
// the athlete taps the mic on the field they're filling. See user request
// 2026-10-24: make logging as easy and automatic as possible, since these
// text fields are the ones that actually require typing.
const MIC_MAX_SECONDS = 90; // hard cap — also what stops a recording left running by accident

function MicButton({ authSession, field, onText }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(MIC_MAX_SECONDS);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const autoStopRef = useRef(null);
  const tickRef = useRef(null);

  const clearTimers = () => {
    if (autoStopRef.current) clearTimeout(autoStopRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
  };

  const start = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        clearTimers();
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append('audio', blob, 'recording.webm');
          fd.append('field', field);
          const token = authSession?.access_token;
          const res = await fetch(`${API}/game-plans/transcribe`, {
            method: 'POST',
            headers: { ...(token && { Authorization: `Bearer ${token}` }) },
            body: fd,
          });
          if (res.status === 429) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body?.detail?.message || body?.detail || 'Daily dictation limit reached for this field.');
          }
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          if (data.text) onText(data.text);
          else setError("Didn't catch anything — try again.");
        } catch (e) {
          setError(e.message || "Couldn't transcribe — try again.");
        }
        setBusy(false);
      };
      recorderRef.current = mr;
      mr.start();
      setRecording(true);
      setSecondsLeft(MIC_MAX_SECONDS);
      // Auto-stop at MIC_MAX_SECONDS — the recording can't be left running
      // (accidentally or otherwise) past this, win or lose on a tab staying
      // open. Ticked countdown so the button itself shows time remaining.
      autoStopRef.current = setTimeout(() => stop(), MIC_MAX_SECONDS * 1000);
      tickRef.current = setInterval(() => setSecondsLeft(s => Math.max(0, s - 1)), 1000);
    } catch (e) {
      setError('Microphone access denied.');
    }
  };

  const stop = () => { recorderRef.current?.stop(); setRecording(false); };

  useEffect(() => () => clearTimers(), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <button type="button" onClick={recording ? stop : start} disabled={busy}
        title={recording ? 'Stop recording' : 'Speech-to-text (max 90s, 5/hour per field)'}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          width: (recording || busy) ? 'auto' : 30, height: 30, minWidth: 30,
          background: recording ? 'rgba(239,68,68,0.15)' : busy ? 'rgba(56,189,248,0.10)' : 'rgba(56,189,248,0.10)',
          border: `1px solid ${recording ? 'rgba(239,68,68,0.5)' : 'rgba(56,189,248,0.35)'}`,
          borderRadius: 15, color: recording ? '#fca5a5' : '#7dd3fc',
          fontSize: 11.5, fontWeight: 600, padding: (recording || busy) ? '4px 10px' : 0, cursor: busy ? 'default' : 'pointer',
          fontFamily: 'inherit', lineHeight: 1,
        }}>
        {busy ? (
          <>
            {/* Transcription is a network round-trip, not instant — a bare
                "…" read as frozen/broken rather than working. Explicit
                "Saving recording…" text (feedback 2026-11-02) makes clear
                the mic button is doing something, same as the red
                recording state below. */}
            <span aria-hidden="true" style={{
              display: 'inline-block', width: 9, height: 9, borderRadius: '50%',
              border: '2px solid currentColor', borderTopColor: 'transparent',
              animation: 'zxMicSpin 0.7s linear infinite',
            }} />
            Saving recording…
          </>
        ) : recording ? (
          <>🔴 Recording speech… {secondsLeft}s</>
        ) : '🎤'}
      </button>
      {(recording || busy) && (
        <style>{'@keyframes zxMicSpin { to { transform: rotate(360deg); } }'}</style>
      )}
      {error && <span style={{ color: '#fca5a5', fontSize: 10.5 }}>{error}</span>}
    </span>
  );
}

// ── Session form modal ────────────────────────────────────────────────────────
function SessionModal({ session, date, userId, authSession, gamePlanSports = [], activeSport, onSaved, onClose, initialTab, prefillSport, readOnly = false }) {
  const isNew = !session?.id;
  const isPast = date < toISO(new Date());
  const isToday = date === toISO(new Date());

  const [tab, setTab]   = useState(initialTab || (session?.result || session?.performance_score ? 'result' : 'plan'));
  // Sync tab when tutorial switches initialTab on an already-open modal
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const [saving, setSaving]     = useState(false);
  const [saveError, setSaveError] = useState(null);
  const saveErrorRef = useRef(null);
  const outcomeRef   = useRef(null);

  // Whenever a save error appears, make sure it's actually visible — the modal is a
  // long scrollable form and a silently-appearing banner below the fold reads as
  // "nothing happened" even though the save was correctly blocked.
  useEffect(() => {
    if (saveError && saveErrorRef.current) {
      saveErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [saveError]);

  // Opponent state
  const [opponents,        setOpponents]        = useState([]);
  const [opponentSearch,   setOpponentSearch]   = useState('');
  const [showOpponentDrop, setShowOpponentDrop] = useState(false);

  // Load opponents for this user
  useEffect(() => {
    if (!userId) return;
    let q = supabase.from('opponents').select('*').eq('user_id', userId);
    if (activeSport) q = q.eq('sport', activeSport);
    q.order('name').then(({ data }) => setOpponents(data || []));
  }, [userId, activeSport]);

  // Doubles partners — same load pattern as opponents.
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    if (!userId) return;
    supabase.from('partners').select('*').eq('user_id', userId)
      .order('name').then(({ data }) => setPartners(data || []));
  }, [userId]);

  // Load active competitions for this user (archived ones don't show up here)
  const [competitions, setCompetitions] = useState([]);
  useEffect(() => {
    if (!userId) return;
    let q = supabase.from('competitions').select('*').eq('user_id', userId).eq('status', 'active');
    if (activeSport) q = q.eq('sport', activeSport);
    q.order('start_date', { ascending: false }).then(({ data }) => setCompetitions(data || []));
  }, [userId, activeSport]);

  // "Preparation notes by Data Coach" — observations drawn from the player's
  // own history, never a drafted plan. See docs_zenanalytics/PRODUCT-GUIDELINES.md §3.
  const [prepNotesOpen,    setPrepNotesOpen]    = useState(false);
  const [prepNotesLoading, setPrepNotesLoading] = useState(false);
  const [prepNotesData,    setPrepNotesData]    = useState(null); // { available, notes, based_on, reason }
  const [prepNotesError,   setPrepNotesError]   = useState(null);

  // "Clean up" on Post-session notes — fixes grammar/filler words on
  // dictated or roughly-typed text without changing what it says.
  const [cleaningUp, setCleaningUp] = useState(false);

  // Escape closes the prep-notes popup specifically (not the whole
  // SessionModal underneath it) while it's open.
  useEffect(() => {
    if (!prepNotesOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setPrepNotesOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prepNotesOpen]);

  const [form, setForm] = useState({
    scheduled_date:      session?.scheduled_date?.slice(0,10) || date || toISO(new Date()),
    // Match, not Training -- a player opens a new plan to log a match far
    // more often than a training session (feedback 2026-11-02).
    type:                session?.type                || 'match',
    // Existing sessions keep their own sport even if it's not the active one
    // (e.g. viewed via search/history) — the lock below only applies to new
    // sessions, which always belong to whichever sport is active right now.
    sport:               session?.sport               || activeSport || prefillSport || 'tennis',
    goal:                session?.goal                || '',
    game_plan:           session?.game_plan           || '',
    opponent_name:       session?.opponent_name       || '',
    opponent_id:         session?.opponent_id         || null,
    match_format:        session?.match_format        || ((prefillSport || '') === 'padel' ? 'doubles' : 'singles'),
    partner_name:        session?.partner_name        || '',
    partner_id:          session?.partner_id          || null,
    opponent2_name:      session?.opponent2_name      || '',
    opponent2_id:        session?.opponent2_id        || null,
    competition_id:      session?.competition_id      || null,
    round_label:         session?.round_label         || '',
    level:               session?.level               || '',
    estimated_duration:  session?.estimated_duration  || '',
    intensity_level:     session?.intensity_level     || null,
    // Pre-match self-check — how ready the player feels going in, not a
    // post-match reflection. Distinct from Competition review's "Mental
    // focus" (a different question over a different timeframe — see
    // docs_zenanalytics/PRODUCT-GUIDELINES.md §3).
    mental_readiness:    session?.mental_readiness    || null,
    physical_readiness:  session?.physical_readiness  || null,
    result:              session?.result              || '',
    match_score:         session?.match_score         || {},
    home_away:           session?.home_away           || '',
    performance_score:   session?.performance_score   || 0,
    game_plan_adherence: session?.game_plan_adherence || 0,
    // Racket-sport match breakdown — the three phases every match is made
    // of, rated separately from the overall performance score.
    serve_rating:        session?.serve_rating        || 0,
    return_rating:       session?.return_rating       || 0,
    forehand_rating:     session?.forehand_rating     || 0,
    backhand_rating:     session?.backhand_rating     || 0,
    net_play_rating:     session?.net_play_rating     || 0,
    actual_duration_minutes: session?.actual_duration_minutes ?? '',
    avg_heart_rate:      session?.avg_heart_rate      ?? '',
    calories:            session?.calories            ?? '',
    post_notes:          session?.post_notes          || '',
    // ── Endurance ──────────────────────────────────────────────────────────────
    brick_sport_2:       session?.brick_sport_2       || '',
    brick_duration_2:    session?.brick_duration_2    || '',
    race_name:           session?.race_name           || '',
    race_distance:       session?.race_distance       || '',
    focus_areas:         session?.focus_areas         || [],
    // Recurring (new sessions only — not persisted, UI-only)
    recurring:           false,
    recur_days:          [],   // ['mon','tue',…]
    recur_until:         '',
    // Which racket/shoes this plan is actually played with — defaults to
    // the player's default equipment (filled in by the effect below for a
    // new plan), but a player with more than one racket or pair of shoes
    // needs to be able to pick a different one. null while loading/unset.
    equipment_racket_id: session?.equipment_racket_id ?? null,
    equipment_shoes_id:  session?.equipment_shoes_id  ?? null,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Active rackets/shoes for the current sport, to populate the pickers
  // below — and, for a brand-new plan, the player's own default equipment
  // (same source handleSave used to snapshot silently before) so the
  // picker opens already showing what would've been chosen automatically.
  const [equipOptions, setEquipOptions] = useState({ rackets: [], shoes: [], defaultRacketId: null, defaultShoesId: null });
  const equipDefaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (!userId || !form.sport) return;
    let cancelled = false;
    (async () => {
      // Defaults are always fetched (not just for a new plan) so the picker
      // can mark which option is the default one — they're only
      // auto-applied onto the form's own fields for a brand-new plan.
      const [{ data: items }, defaults] = await Promise.all([
        supabase.from('equipment_items').select('id,brand,model,type,active').eq('user_id', userId).eq('sport', form.sport),
        getDefaultEquipmentSnapshot(userId, form.sport),
      ]);
      if (cancelled) return;
      const active = (items || []).filter(i => i.active !== false);
      setEquipOptions({
        rackets: active.filter(i => i.type === 'racket'),
        shoes: active.filter(i => i.type === 'shoes'),
        defaultRacketId: defaults?.equipment_racket_id || null,
        defaultShoesId: defaults?.equipment_shoes_id || null,
      });
      if (defaults && isNew && !equipDefaultsAppliedRef.current) {
        equipDefaultsAppliedRef.current = true;
        setForm(f => ({ ...f, equipment_racket_id: defaults.equipment_racket_id, equipment_shoes_id: defaults.equipment_shoes_id }));
      }
    })();
    return () => { cancelled = true; };
  }, [userId, form.sport, isNew]);

  const fetchPrepNotes = async () => {
    setPrepNotesOpen(true);
    setPrepNotesLoading(true);
    setPrepNotesError(null);
    setPrepNotesData(null);
    try {
      const token = authSession?.access_token;
      const res = await fetch(`${API}/game-plans/prep-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          sport: form.sport,
          type: form.type,
          opponent_name: form.opponent_name || null,
          opponent_id: form.opponent_id || null,
          competition_id: form.competition_id || null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPrepNotesData(data);
    } catch (e) {
      setPrepNotesError("Couldn't load preparation notes right now — try again in a moment.");
    }
    setPrepNotesLoading(false);
  };

  const cleanupPostNotes = async () => {
    const text = (form.post_notes || '').trim();
    if (!text) return;
    setCleaningUp(true);
    try {
      const token = authSession?.access_token;
      const res = await fetch(`${API}/game-plans/cleanup-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.text) set('post_notes', data.text.slice(0, 4000));
    } catch (e) {
      console.error('[cleanupPostNotes] failed:', e);
    }
    setCleaningUp(false);
  };

  // Inserts a note as clearly-sourced raw material, not as a finished plan —
  // see docs_zenanalytics/PRODUCT-GUIDELINES.md §3's "observation vs.
  // directive" rule. Appends rather than overwrites, and the player still
  // has to write what they're actually going to do about it.
  const insertPrepNote = (note) => {
    const line = `Data Coach: ${note}`;
    set('game_plan', (form.game_plan ? `${form.game_plan}\n${line}` : line).slice(0, 600));
  };

  // Padel is played almost exclusively as doubles — default the Format toggle
  // to Doubles when padel is picked, but only until the athlete actually
  // touches the toggle themselves (or when editing a session that already
  // has a saved format), so this default never overrides a real choice.
  const formatTouchedRef = useRef(!isNew);
  const setMatchFormat = (fmt) => { formatTouchedRef.current = true; set('match_format', fmt); };

  const handleSportChange = (sport) => {
    const formatPatch = (!formatTouchedRef.current && sport === 'padel') ? { match_format: 'doubles' } : {};
    setForm(f => ({ ...f, sport, ...formatPatch }));
  };

  const sportType = getSportType(form.sport);
  const showOpponent = ['match','competition'].includes(form.type);
  const showDoublesToggle = showOpponent && supportsDoubles(form.sport);
  const isDoubles = showDoublesToggle && form.match_format === 'doubles';
  const showScore = showOpponent || ['tennis','padel','squash','badminton','football',
    'basketball','volleyball','hockey','golf'].includes(form.sport);

  const handleSave = async () => {
    setSaveError(null);

    // A brand-new session saved before the profile's active sport has
    // loaded falls back to the literal 'tennis' default (see `form.sport`
    // above) instead of the athlete's real sport — same class of bug as
    // CompetitionFormModal's sport=null issue, but worse here since it
    // mis-tags the row with a WRONG sport rather than leaving it empty.
    // Found 2026-10-25 while auditing that bug for the same regression
    // elsewhere; no evidence it has actually happened yet (checked every
    // sport-test account), but block it going forward.
    if (isNew && !session?.sport && !activeSport && !prefillSport) {
      setSaveError('Still loading your sport — wait a moment and try again.');
      return;
    }

    // Require outcome for match/competition — training can be saved without a result
    if (!isNew && showOpponent && tab === 'result' && (isPast || isToday) && !form.result) {
      setSaveError('Please log the match outcome (Win / Loss / Draw) before saving.');
      outcomeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSaving(true);

    try {
      if (!userId) {
        throw new Error('Not authenticated. Please log out and log in again.');
      }

      // Defense in depth against the "tab was idle, token went stale" case
      // (see supabaseClient.js) — resolve the current session (which refreshes
      // it under the hood if needed) before attempting the write, so a slow
      // background refresh can't race against the save itself.
      await Promise.race([
        supabase.auth.getSession(),
        new Promise(resolve => setTimeout(resolve, 4000)), // don't block save forever if this itself stalls
      ]);

      // What racket/shoes this plan is actually played with — the picker
      // above starts out showing the player's default equipment (same
      // snapshot this used to take silently), but form.equipment_racket_id/
      // equipment_shoes_id is now the authoritative, editable value: a
      // player with more than one racket or pair of shoes can pick a
      // different one per plan instead of always getting their default.
      let stringingId = null;
      if (form.equipment_racket_id) {
        const { data: str } = await supabase
          .from('equipment_stringings')
          .select('id')
          .eq('racket_id', form.equipment_racket_id)
          .is('archived_at', null)
          .order('strung_date', { ascending: false })
          .limit(1)
          .maybeSingle();
        stringingId = str?.id || null;
      }
      const equipmentSnapshot = {
        equipment_racket_id: form.equipment_racket_id || null,
        equipment_stringing_id: stringingId,
        equipment_shoes_id: form.equipment_shoes_id || null,
      };

      // Payload inside try — so any unexpected JS error here is caught and saving resets
      const payload = {
        user_id:             userId,
        scheduled_date:      form.scheduled_date || date,
        type:                form.type,
        sport:               form.sport || null,
        goal:                (form.goal || '').trim() || null,
        game_plan:           (form.game_plan || '').trim() || null,
        opponent_name:       showOpponent ? ((form.opponent_name || '').trim() || null) : null,
        opponent_id:         showOpponent ? (form.opponent_id || null) : null,
        match_format:        isDoubles ? 'doubles' : 'singles',
        partner_name:        isDoubles ? ((form.partner_name || '').trim() || null) : null,
        partner_id:          isDoubles ? (form.partner_id || null) : null,
        opponent2_name:      isDoubles ? ((form.opponent2_name || '').trim() || null) : null,
        opponent2_id:        isDoubles ? (form.opponent2_id || null) : null,
        competition_id:      form.competition_id || null,
        round_label:         (form.round_label || '').trim() || null,
        level:               (form.level || '').trim() || null,
        estimated_duration:  form.estimated_duration ? parseInt(form.estimated_duration) : null,
        intensity_level:     form.intensity_level || null,
        mental_readiness:    form.mental_readiness || null,
        physical_readiness:  form.physical_readiness || null,
        result:              form.result || null,
        match_score:         (form.match_score && Object.keys(form.match_score).length > 0) ? form.match_score : null,
        home_away:           (showOpponent && isTeamSport(form.sport) && form.home_away) ? form.home_away : null,
        performance_score:   form.performance_score || null,
        game_plan_adherence: form.game_plan_adherence || null,
        serve_rating:        showOpponent ? (form.serve_rating || null) : null,
        return_rating:       showOpponent ? (form.return_rating || null) : null,
        forehand_rating:     showOpponent ? (form.forehand_rating || null) : null,
        backhand_rating:     showOpponent ? (form.backhand_rating || null) : null,
        net_play_rating:     showOpponent ? (form.net_play_rating || null) : null,
        actual_duration_minutes: form.actual_duration_minutes !== '' && form.actual_duration_minutes != null ? parseInt(form.actual_duration_minutes) || null : null,
        avg_heart_rate:      form.avg_heart_rate !== '' && form.avg_heart_rate != null ? parseInt(form.avg_heart_rate) || null : null,
        calories:            form.calories !== '' && form.calories != null ? parseInt(form.calories) || null : null,
        post_notes:          (form.post_notes || '').trim() || null,
        ...equipmentSnapshot,
        // Endurance fields
        brick_sport_2:       (form.type === 'brick' && form.brick_sport_2) ? form.brick_sport_2 : null,
        brick_duration_2:    (form.type === 'brick' && form.brick_duration_2) ? parseInt(form.brick_duration_2) : null,
        race_name:           (form.type === 'race_day' && form.race_name) ? (form.race_name || '').trim() : null,
        race_distance:       (form.type === 'race_day' && form.race_distance) ? form.race_distance : null,
        // plan_goals is intentionally NOT written here anymore — "In the scope
        // today" (game_plan) replaces it going forward. Existing rows keep
        // their historical plan_goals content untouched (see spec §1.2/§3);
        // omitting the key from this payload means Supabase's partial update
        // never touches that column.
        focus_areas:         (showOpponent && form.focus_areas?.length > 0) ? form.focus_areas : null,
        completed_at:        form.result
                               ? new Date().toISOString()
                               : (session?.completed_at || null),
      };
      console.log('[handleSave] payload:', { isNew, id: session?.id, userId, type: payload.type });

      // Build list of payloads — one for normal save, many for recurring
      let payloads = [payload];
      if (isNew && form.recurring && form.recur_days.length > 0 && form.recur_until) {
        const DAY_MAP = { mon:1, tue:2, wed:3, thu:4, fri:5, sat:6, sun:0 };
        const until = new Date(form.recur_until + 'T12:00:00');
        const extra = [];
        const cursor = new Date((form.scheduled_date || date) + 'T12:00:00');
        cursor.setDate(cursor.getDate() + 1); // start day after first session
        while (cursor <= until) {
          const dayName = ['sun','mon','tue','wed','thu','fri','sat'][cursor.getDay()];
          if (form.recur_days.includes(dayName)) {
            extra.push({ ...payload, scheduled_date: toISO(cursor) });
          }
          cursor.setDate(cursor.getDate() + 1);
        }
        payloads = [payload, ...extra];
      }

      // Auto-create opponent if name given but no id yet
      if (showOpponent && payload.opponent_name && !payload.opponent_id) {
        const existing = opponents.find(o => o.name.toLowerCase() === payload.opponent_name.toLowerCase());
        if (existing) {
          payload.opponent_id = existing.id;
        } else {
          // Same timeout guard as the main save below — this insert was
          // previously unprotected, so a stalled request here could hang the
          // whole save forever with no error ever shown.
          const { data: newOpp } = await Promise.race([
            supabase.from('opponents').insert({
              user_id: userId, name: payload.opponent_name, sport: form.sport,
              type: 'player',
            }).select().single(),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Creating opponent timed out after 10s. Check your connection.')), 10000)
            ),
          ]);
          if (newOpp) {
            payload.opponent_id = newOpp.id;
            setOpponents(prev => [...prev, newOpp]);
          }
        }
        payloads = payloads.map(p => ({ ...p, opponent_id: payload.opponent_id }));
      }

      // Same auto-create pattern for the second opponent (doubles) — reuses
      // the same `opponents` table so head-to-head stats stay unified
      // regardless of which side of a doubles match someone was logged on.
      if (isDoubles && payload.opponent2_name && !payload.opponent2_id) {
        const existing = opponents.find(o => o.name.toLowerCase() === payload.opponent2_name.toLowerCase());
        if (existing) {
          payload.opponent2_id = existing.id;
        } else {
          const { data: newOpp2 } = await Promise.race([
            supabase.from('opponents').insert({
              user_id: userId, name: payload.opponent2_name, sport: form.sport, type: 'player',
            }).select().single(),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Creating opponent timed out after 10s. Check your connection.')), 10000)
            ),
          ]);
          if (newOpp2) {
            payload.opponent2_id = newOpp2.id;
            setOpponents(prev => [...prev, newOpp2]);
          }
        }
        payloads = payloads.map(p => ({ ...p, opponent2_id: payload.opponent2_id }));
      }

      // Same pattern again for the doubles partner, against the `partners` table.
      if (isDoubles && payload.partner_name && !payload.partner_id) {
        const existingPartner = partners.find(p => p.name.toLowerCase() === payload.partner_name.toLowerCase());
        if (existingPartner) {
          payload.partner_id = existingPartner.id;
        } else {
          const { data: newPartner } = await Promise.race([
            supabase.from('partners').insert({ user_id: userId, name: payload.partner_name }).select().single(),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Creating partner timed out after 10s. Check your connection.')), 10000)
            ),
          ]);
          if (newPartner) {
            payload.partner_id = newPartner.id;
            setPartners(prev => [...prev, newPartner]);
          }
        }
        payloads = payloads.map(p => ({ ...p, partner_id: payload.partner_id }));
      }

      const dbQuery = isNew
        ? supabase.from('session_plans').insert(payloads).select()
        : supabase.from('session_plans').update(payload).eq('id', session.id).select();

      const { error, data } = await Promise.race([
        dbQuery,
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Save timed out after 20s. Check your connection.')), 20000)
        ),
      ]);
      console.log('[handleSave] result:', { error, rows: data?.length });
      if (error) throw error;
      onSaved(data?.[0] ?? null);
    } catch (e) {
      console.error('[handleSave] error:', e);
      const msg = e?.message || e?.details || e?.hint || JSON.stringify(e) || 'Save failed — please try again.';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!(await confirmDialog('Delete this game plan? It is kept and can be restored from "Recently deleted".', { confirmLabel: 'Delete' }))) return;
    const { error } = await supabase.from('session_plans').update({ deleted_at: new Date().toISOString() }).eq('id', session.id);
    if (error) { alert('Delete failed: ' + error.message); return; }
    onSaved();
  };

  const inp = {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.09)', border: `1px solid ${C.border}`,
    borderRadius: 8, padding: '8px 12px', color: C.text, fontSize: 14,
    fontFamily: C.font, outline: 'none',
  };
  const lbl = { fontSize: 11, color: C.text, marginBottom: 4, display: 'block',
                fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' };

  // Live character counter shown under a text field — turns amber near the limit,
  // red at the limit, so hitting the cap is never a silent surprise.
  const CharCounter = ({ value, max }) => {
    const len = (value || '').length;
    const pct = len / max;
    const color = pct >= 1 ? '#ef4444' : pct >= 0.9 ? '#f59e0b' : C.muted;
    return (
      <div style={{ textAlign: 'right', fontSize: 11, color, marginTop: 3 }}>
        {len} / {max}
      </div>
    );
  };

  return (
    <>
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      overflow: 'hidden',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#111827', borderRadius: '18px 18px 0 0',
        width: '100%', maxWidth: 580, maxHeight: '92vh', overflowY: 'auto',
        overscrollBehavior: 'contain',
        padding: '20px 20px 44px',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.5)',
        position: 'relative',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ color: C.text, fontSize: 11 }}>
              {date > toISO(new Date()) ? '🎯 Plan ahead' : isToday ? '🎯 Today' : '📋 Past session'}
            </div>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 17 }}>
              {isNew ? `+ Session — ${fmtDate(date)}` : `${fmtDate(date)}`}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>

        {/* Plan / Result tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: 4 }}>
          {[
            { id: 'plan',   label: '📋 Plan' },
            { id: 'result', label: '📊 Result', disabled: isNew },
          ].map(t => (
            <button key={t.id} onClick={() => !t.disabled && setTab(t.id)} style={{
              flex: 1, padding: '8px 0', borderRadius: 8, border: 'none', cursor: t.disabled ? 'default' : 'pointer',
              background: tab === t.id ? C.accent : 'transparent',
              color: tab === t.id ? '#fff' : t.disabled ? C.border : C.muted,
              fontWeight: 600, fontSize: 13, fontFamily: C.font,
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Glass pane overlay — covers form content but not header/tabs */}
        {readOnly && (
          <div style={{
            position: 'absolute', left: 0, right: 0, bottom: 0,
            top: 130, zIndex: 10,
            cursor: 'default',
          }} />
        )}

        {/* ── PLAN TAB ── */}
        {tab === 'plan' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Date */}
            <div>
              <span style={lbl}>Date</span>
              <input
                type="date"
                value={form.scheduled_date || ''}
                onChange={e => set('scheduled_date', e.target.value)}
                style={{ ...inp, colorScheme: 'dark' }}
              />
            </div>

            {/* Type */}
            <div>
              <span style={lbl}>Type</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {[
                  // Match first, and the form's own default (below) -- a
                  // player opens this to log a match far more often than a
                  // training session, so that's the one-tap path. Match
                  // analytics is the core product, but practice sessions are
                  // logged too — Data Coach needs to see what was trained,
                  // not just match outcomes. "Competition" used to be a
                  // separate type here, but that's now handled properly by
                  // linking a match to a Competition (league/tournament)
                  // instead of a second flavor of match.
                  { v: 'match',       icon: '⚔️', label: 'Match' },
                  { v: 'training',    icon: '🏋️', label: 'Training' },
                ].map(t => (
                  <button key={t.v} onClick={() => set('type', t.v)} style={{
                    flex: 1, padding: '8px 4px', borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${form.type === t.v ? C.accent : C.border}`,
                    background: form.type === t.v ? `${C.accent}18` : 'transparent',
                    color: form.type === t.v ? C.accent : C.muted,
                    fontSize: 12, fontWeight: 600, fontFamily: C.font,
                  }}>
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sport — locked to the active sport for new sessions. Full
                isolation between sports was an explicit decision (2026-10-18,
                PRODUCT-GUIDELINES.md §5): "not even in the selectors" — so a
                new session can't accidentally get created under the wrong
                sport. Switch the active sport from the header to log
                something in a different one. Existing sessions (editing)
                still show their own sport as a locked label too, since
                changing a saved session's sport is a separate, bigger
                operation (its competition/season links may no longer make
                sense) — not something to allow from this quick field. */}
            <div>
              <span style={lbl}>Sport</span>
              {isNew && activeSport ? (
                <div style={{
                  ...inp, background: '#1e2736', color: C.text, display: 'flex',
                  alignItems: 'center', gap: 8, cursor: 'default',
                }}>
                  <span>{SPORTS.find(s => s.value === activeSport)?.label || activeSport}</span>
                  <span style={{ marginLeft: 'auto', color: C.muted, fontSize: 11 }}>Active sport</span>
                </div>
              ) : !isNew ? (
                <div style={{
                  ...inp, background: '#1e2736', color: C.text, display: 'flex',
                  alignItems: 'center', gap: 8, cursor: 'default',
                }}>
                  <span>{SPORTS.find(s => s.value === form.sport)?.label || form.sport}</span>
                </div>
              ) : (() => {
                // Fallback for contexts with no activeSport yet (e.g. demo
                // mode) — old behaviour, pick from every tracked sport.
                const filteredSports = (gamePlanSports || []).length > 0
                  ? SPORTS.filter(s => gamePlanSports.includes(s.value) || s.value === 'other')
                  : SPORTS;
                const grouped = filteredSports.reduce((acc, s) => {
                  const g = s.group || 'Other';
                  if (!acc[g]) acc[g] = [];
                  acc[g].push(s);
                  return acc;
                }, {});
                return (
                  <select value={form.sport} onChange={e => handleSportChange(e.target.value)}
                    style={{ ...inp, background: '#1e2736', color: C.text }}>
                    <option value="" style={{ background: '#1e2736', color: C.text }}>— select sport —</option>
                    {Object.entries(grouped).map(([group, sports]) => (
                      <optgroup key={group} label={group} style={{ background: '#1e2736', color: C.muted }}>
                        {sports.map(s => (
                          <option key={s.value} value={s.value}
                            style={{ background: '#1e2736', color: C.text }}>{s.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                );
              })()}
            </div>

            {/* Racket/shoes for this plan — starts on the player's default
                equipment (snapshotted into the fields above once loaded),
                but a player with more than one racket or pair of shoes
                needs to say otherwise here. Only shown once there's
                something to actually choose between; a single-racket
                player never sees an empty picker. Feedback 2026-11-02. */}
            {(equipOptions.rackets.length > 0 || equipOptions.shoes.length > 0) && (
              <div style={{ display: 'flex', gap: 10 }}>
                {equipOptions.rackets.length > 0 && (
                  <div style={{ flex: 1 }}>
                    <span style={lbl}>{racketLabel(form.sport)}</span>
                    <select value={form.equipment_racket_id || ''} onChange={e => set('equipment_racket_id', e.target.value || null)}
                      style={{ ...inp, background: '#1e2736', color: C.text }}>
                      <option value="" style={{ background: '#1e2736', color: C.text }}>— none —</option>
                      {equipOptions.rackets.map(r => (
                        <option key={r.id} value={r.id} style={{ background: '#1e2736', color: C.text }}>
                          {r.brand} {r.model}{r.id === equipOptions.defaultRacketId ? ' (Default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {equipOptions.shoes.length > 0 && (
                  <div style={{ flex: 1 }}>
                    <span style={lbl}>Shoes</span>
                    <select value={form.equipment_shoes_id || ''} onChange={e => set('equipment_shoes_id', e.target.value || null)}
                      style={{ ...inp, background: '#1e2736', color: C.text }}>
                      <option value="" style={{ background: '#1e2736', color: C.text }}>— none —</option>
                      {equipOptions.shoes.map(s => (
                        <option key={s.id} value={s.id} style={{ background: '#1e2736', color: C.text }}>
                          {s.brand} {s.model}{s.id === equipOptions.defaultShoesId ? ' (Default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Singles / Doubles — only shown for sports doubles is actually played
                in (see DOUBLES_SPORTS in utils/matchFormat.js). Driving this off an
                explicit toggle rather than inferring it from opponent2 being filled
                in lets the form ask up front and show the right fields next. */}
            {showDoublesToggle && (
              <div>
                <span style={lbl}>Format</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['singles', 'doubles'].map(fmt => (
                    <button key={fmt} type="button"
                      onClick={() => setMatchFormat(fmt)}
                      style={{
                        background: form.match_format === fmt ? 'rgba(16,185,129,0.18)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${form.match_format === fmt ? C.accent : C.border}`,
                        color: form.match_format === fmt ? C.accent : C.muted,
                        borderRadius: 8, padding: '7px 14px', fontSize: 12.5, fontWeight: 600,
                        cursor: 'pointer', fontFamily: C.font, textTransform: 'capitalize',
                      }}>{fmt}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Opponent */}
            {showOpponent && (
              <div style={{ position: 'relative' }}>
                <span style={lbl}>{isDoubles ? 'Opponent 1' : 'Opponent / team / competition (optional)'}</span>
                <input
                  value={opponentSearch || form.opponent_name}
                  onChange={e => {
                    setOpponentSearch(e.target.value);
                    set('opponent_name', e.target.value);
                    set('opponent_id', null);
                    setShowOpponentDrop(true);
                  }}
                  onFocus={() => setShowOpponentDrop(true)}
                  onBlur={() => setTimeout(() => setShowOpponentDrop(false), 150)}
                  placeholder="Search or type a new name…"
                  style={inp}
                  autoComplete="off"
                />
                {showOpponentDrop && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                    background: '#1e2736', border: `1px solid ${C.border}`, borderRadius: 8,
                    maxHeight: 180, overflowY: 'auto', marginTop: 2,
                  }}>
                    {opponents
                      .filter(o => !opponentSearch || o.name.toLowerCase().includes(opponentSearch.toLowerCase()))
                      .map(o => (
                        <div key={o.id}
                          onMouseDown={() => {
                            set('opponent_name', o.name);
                            set('opponent_id', o.id);
                            setOpponentSearch('');
                            setShowOpponentDrop(false);
                          }}
                          style={{
                            padding: '9px 12px', cursor: 'pointer', fontSize: 13, color: C.text,
                            borderBottom: `1px solid ${C.border}`,
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <span style={{ color: C.muted, fontSize: 11, marginRight: 8 }}>
                            {o.type === 'team' ? '🏟️' : o.type === 'event' ? '🏆' : '👤'}
                          </span>
                          {o.name}
                        </div>
                      ))
                    }
                    {opponentSearch && !opponents.find(o => o.name.toLowerCase() === opponentSearch.toLowerCase()) && (
                      <div style={{ padding: '9px 12px', fontSize: 12, color: C.muted, fontStyle: 'italic' }}>
                        ✨ "{opponentSearch}" will be saved as a new opponent
                      </div>
                    )}
                    {opponents.length === 0 && !opponentSearch && (
                      <div style={{ padding: '9px 12px', fontSize: 12, color: C.muted }}>
                        Type a name to add your first opponent
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {isDoubles && (
              <div>
                <span style={lbl}>Opponent 2</span>
                <input
                  value={form.opponent2_name}
                  onChange={e => { set('opponent2_name', e.target.value); set('opponent2_id', null); }}
                  placeholder="Search or type a new name…"
                  list="opponents-datalist"
                  style={inp}
                  autoComplete="off"
                />
                <datalist id="opponents-datalist">
                  {opponents.map(o => <option key={o.id} value={o.name} />)}
                </datalist>
              </div>
            )}

            {isDoubles && (
              <div>
                <span style={lbl}>Your partner (optional)</span>
                <input
                  value={form.partner_name}
                  onChange={e => { set('partner_name', e.target.value); set('partner_id', null); }}
                  placeholder="Search or type a new name…"
                  list="partners-datalist"
                  style={inp}
                  autoComplete="off"
                />
                <datalist id="partners-datalist">
                  {partners.map(p => <option key={p.id} value={p.name} />)}
                </datalist>
              </div>
            )}

            {/* Level — only for match/competition */}
            {showOpponent && (
              <div>
                <span style={lbl}>Level (optional)</span>
                <input value={form.level} onChange={e => set('level', e.target.value)}
                  placeholder="e.g. 4.0 UTR, club level, national, friendly…" style={inp} />
              </div>
            )}

            {/* Competition — links this session to a league/tournament/goal */}
            {competitions.length > 0 && (
              <div>
                <span style={lbl}>Competition (optional)</span>
                <select
                  value={form.competition_id || ''}
                  onChange={e => set('competition_id', e.target.value || null)}
                  style={inp}
                >
                  <option value="">— None —</option>
                  {competitions.map(c => (
                    <option key={c.id} value={c.id}>{competitionIcon(c.type)} {c.name}</option>
                  ))}
                </select>
              </div>
            )}
            {form.competition_id && (
              <div>
                <span style={lbl}>Round (optional)</span>
                <input value={form.round_label} onChange={e => set('round_label', e.target.value)}
                  placeholder="e.g. Kierros 16, Quarter final, Week 3" style={inp} />
              </div>
            )}

            {/* Duration + Intensity side by side */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <span style={lbl}>Estimated duration (min)</span>
                <input type="number" value={form.estimated_duration}
                  onChange={e => set('estimated_duration', e.target.value)}
                  placeholder="60" style={inp} />
              </div>
              <div>
                <span style={lbl}>Intensity level (1–5)</span>
                <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                  {[1,2,3,4,5].map(n => {
                    const il = INTENSITY_LABELS[n];
                    const active = form.intensity_level === n;
                    return (
                      <button key={n} onClick={() => set('intensity_level', active ? null : n)} style={{
                        flex: 1, padding: '6px 0', borderRadius: 6, cursor: 'pointer', border: 'none',
                        background: active ? il.color : 'rgba(255,255,255,0.06)',
                        color: active ? '#fff' : C.muted, fontSize: 12, fontWeight: 700,
                        fontFamily: C.font,
                      }} title={`${il.label}: ${il.desc}`}>
                        {n}
                      </button>
                    );
                  })}
                </div>
                {form.intensity_level && (
                  <div style={{ color: INTENSITY_LABELS[form.intensity_level].color, fontSize: 10, marginTop: 3 }}>
                    {INTENSITY_LABELS[form.intensity_level].label}
                  </div>
                )}
              </div>
            </div>

            {/* Pre-match self-check — optional, player's own read of how
                ready they feel going in. Deliberately separate from
                Competition review's "Mental focus" (a reflection over a
                whole competition, not a single session's starting state).
                See docs_zenanalytics/PRODUCT-GUIDELINES.md §3. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <span style={lbl}>Mental readiness <span style={{ color: C.muted, fontWeight: 400, textTransform: 'none' }}>(optional)</span></span>
                <div style={{ marginTop: 4 }}>
                  <StarRating value={form.mental_readiness} onChange={v => set('mental_readiness', v)} />
                </div>
              </div>
              <div>
                <span style={lbl}>Physical readiness <span style={{ color: C.muted, fontWeight: 400, textTransform: 'none' }}>(optional)</span></span>
                <div style={{ marginTop: 4 }}>
                  <StarRating value={form.physical_readiness} onChange={v => set('physical_readiness', v)} />
                </div>
              </div>
            </div>

            {/* Match/session goal — headline only. See
                docs_zenanalytics/08-GAME-PLAN-LOGIC-REWRITE-2026-09-27.md §1.2:
                "competition" is deliberately dropped from this label even for
                type==='competition' sessions — that word belongs to the
                competition entity's own goal (competitions.goal), not to a
                single session inside it. */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ ...lbl, marginBottom: 0 }}>{form.type === 'training' ? 'Session goal' : 'Match goal'}</span>
                {!readOnly && (
                  <MicButton authSession={authSession} field="goal" onText={t =>
                    set('goal', (form.goal ? `${form.goal} ${t}` : t).slice(0, 400))
                  } />
                )}
              </div>
              <textarea value={form.goal} onChange={e => set('goal', e.target.value.slice(0, 400))}
                maxLength={400}
                placeholder={form.type !== 'training' ? 'e.g. Win the match, stay competitive, execute tactics…' : 'e.g. Long easy session, build match fitness…'}
                rows={2} style={{ ...inp, resize: 'vertical' }} />
              <CharCounter value={form.goal} max={400} />
            </div>

            {/* "In the scope today" — consolidated tactical detail. Replaces the
                old "Sport-specific targets" (Tactical focus / Key skills /
                Target sets-result goal) and "Tactical notes" boxes, which
                overlapped each other and this field. See spec §1.2-1.3. */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ ...lbl, marginBottom: 0 }}>In the scope today</span>
                {!readOnly && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <MicButton authSession={authSession} field="game_plan" onText={t =>
                      set('game_plan', (form.game_plan ? `${form.game_plan} ${t}` : t).slice(0, 600))
                    } />
                    <button type="button" onClick={fetchPrepNotes} style={{
                      background: 'rgba(139,92,246,0.10)', border: '1px solid rgba(139,92,246,0.35)',
                      borderRadius: 7, color: '#c4b5fd', fontSize: 11, fontWeight: 600,
                      padding: '4px 9px', cursor: 'pointer', fontFamily: C.font, whiteSpace: 'nowrap',
                    }}>💬 Preparation notes by Data Coach</button>
                  </span>
                )}
              </div>
              <textarea value={form.game_plan} onChange={e => set('game_plan', e.target.value.slice(0, 600))}
                maxLength={600}
                placeholder="e.g. Aggressive first ball attack, serve-volley pattern, net approaches…"
                rows={3} style={{ ...inp, resize: 'vertical' }} />
              <CharCounter value={form.game_plan} max={600} />
            </div>

            {/* Main focus areas — structured, links to the same breakdown
                categories the Result tab rates every match on, so the review
                screen can highlight what was actually planned. Only meaningful
                where those ratings are collected (match/competition). */}
            {showOpponent && (
              <div>
                <span style={lbl}>Main focus areas (optional)</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[
                    { key: 'serve', label: 'Serve' },
                    { key: 'return', label: 'Return' },
                    { key: 'forehand', label: 'Forehand' },
                    { key: 'backhand', label: 'Backhand' },
                    { key: 'net_play', label: netPlayLabel(form.sport) },
                  ].map(({ key, label }) => {
                    const active = (form.focus_areas || []).includes(key);
                    return (
                      <button key={key} type="button"
                        onClick={() => {
                          const cur = form.focus_areas || [];
                          set('focus_areas', active ? cur.filter(k => k !== key) : [...cur, key]);
                        }}
                        style={{
                          background: active ? 'rgba(16,185,129,0.18)' : 'rgba(255,255,255,0.05)',
                          border: `1px solid ${active ? C.accent : C.border}`,
                          color: active ? C.accent : C.muted,
                          borderRadius: 8, padding: '6px 12px', fontSize: 12.5, fontWeight: 600,
                          cursor: 'pointer', fontFamily: C.font,
                        }}>{active ? '✓ ' : ''}{label}</button>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        )}

        {/* ── RESULT TAB ── */}
        {tab === 'result' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {/* Score / result entry — sport-specific */}
            {(showScore || sportType !== 'none') && (
              <div>
                <span style={lbl}>Result / score</span>
                {(sportType === 'sets') && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {showOpponent && isTeamSport(form.sport) && (
                      <div>
                        <div style={{ color: C.muted, fontSize: 11, marginBottom: 6 }}>Home or away?</div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {[{ val: 'home', label: 'Home' }, { val: 'away', label: 'Away' }].map(loc => {
                            const active = form.home_away === loc.val;
                            return (
                              <button key={loc.val} onClick={() => set('home_away', active ? '' : loc.val)}
                                style={{ padding: '5px 14px', borderRadius: 99, fontSize: 12, fontWeight: active ? 700 : 400,
                                  border: `1px solid ${active ? '#38bdf8' : C.border}`,
                                  background: active ? 'rgba(56,189,248,0.12)' : 'transparent',
                                  color: active ? '#38bdf8' : C.muted, cursor: 'pointer', fontFamily: C.font }}>
                                {loc.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <SetsEntry sport={form.sport} value={form.match_score} onChange={v => set('match_score', v)} />
                  </div>
                )}
              </div>
            )}

            {/* Match outcome (for competitive sports with opponent) — required, so a
                second tap on the already-selected button must NOT clear it. That
                silent-clear was the root cause of "save doesn't work" reports: a
                stray double-tap unset the outcome and blocked the save with no
                obvious visual cue. */}
            {showOpponent && (
              <div ref={outcomeRef}>
                <span style={lbl}>Outcome {!form.result && <span style={{ color: '#f59e0b', fontWeight: 400, textTransform: 'none' }}>— required</span>}</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[
                    { v: 'win',  label: '🏆 Win',  color: '#22c55e' },
                    { v: 'loss', label: '❌ Loss', color: '#ef4444' },
                    { v: 'draw', label: '🤝 Draw', color: '#94a3b8' },
                  ].map(r => (
                    <button key={r.v} onClick={() => set('result', r.v)} style={{
                      flex: 1, padding: '8px 0', borderRadius: 8, cursor: 'pointer',
                      border: `1px solid ${form.result === r.v ? r.color : C.border}`,
                      background: form.result === r.v ? `${r.color}22` : 'transparent',
                      color: form.result === r.v ? r.color : C.muted,
                      fontSize: 13, fontWeight: 700, fontFamily: C.font,
                    }}>{r.label}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Training outcome — optional, simple buttons */}
            {!showOpponent && (
              <div>
                <span style={lbl}>Session outcome <span style={{ color: C.muted, fontWeight: 400, textTransform: 'none' }}>(optional)</span></span>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[
                    { v: 'achieved',     label: '✅ Completed',    color: '#22c55e' },
                    { v: 'not_achieved', label: '⚠️ Incomplete',   color: '#f59e0b' },
                  ].map(r => (
                    <button key={r.v} onClick={() => set('result', form.result === r.v ? '' : r.v)} style={{
                      flex: 1, padding: '8px 0', borderRadius: 8, cursor: 'pointer',
                      border: `1px solid ${form.result === r.v ? r.color : C.border}`,
                      background: form.result === r.v ? `${r.color}18` : 'transparent',
                      color: form.result === r.v ? r.color : C.muted,
                      fontSize: 13, fontWeight: 700, fontFamily: C.font,
                    }}>{r.label}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Rate overall performance */}
            <div>
              <span style={lbl}>Rate your overall performance</span>
              <StarRating value={form.performance_score} onChange={v => set('performance_score', v)} />
            </div>

            {/* Rate game plan adherence */}
            <div>
              <span style={lbl}>How well did you stick to the game plan?</span>
              <StarRating value={form.game_plan_adherence} onChange={v => set('game_plan_adherence', v)} />
            </div>

            {/* Match breakdown — serve, return, forehand, backhand and net play,
                the parts every racket-sport match is made of. Only meaningful
                against an opponent, so it's scoped to showOpponent like the
                Outcome buttons above. */}
            {showOpponent && (
              <div>
                <span style={lbl}>Break it down <span style={{ color: C.muted, fontWeight: 400, textTransform: 'none' }}>(optional)</span></span>
                {(form.focus_areas || []).length > 0 && (
                  <div style={{ color: C.accent, fontSize: 11, marginTop: 2, marginBottom: 6 }}>
                    🎯 marks what you planned to focus on today
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
                  {[
                    { key: 'serve',    icon: '🎾', label: 'Serve',    field: 'serve_rating' },
                    { key: 'return',   icon: '↩️', label: 'Return',   field: 'return_rating' },
                    { key: 'forehand', icon: '💪', label: 'Forehand', field: 'forehand_rating' },
                    { key: 'backhand', icon: '🤚', label: 'Backhand', field: 'backhand_rating' },
                    { key: 'net_play', icon: netPlayIcon(form.sport), label: netPlayLabel(form.sport), field: 'net_play_rating' },
                  ].map(({ key, icon, label, field }) => {
                    const isFocusArea = (form.focus_areas || []).includes(key);
                    return (
                      <div key={key} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                        padding: isFocusArea ? '4px 8px' : '4px 0',
                        borderRadius: 8,
                        background: isFocusArea ? 'rgba(16,185,129,0.10)' : 'transparent',
                        border: isFocusArea ? `1px solid ${C.accent}55` : '1px solid transparent',
                      }}>
                        <span style={{ color: isFocusArea ? C.accent : C.muted, fontSize: 12.5, fontWeight: isFocusArea ? 700 : 400 }}>
                          {isFocusArea ? '🎯 ' : ''}{icon} {label}
                        </span>
                        <StarRating value={form[field]} onChange={v => set(field, v)} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Optional physical data */}
            <div>
              <span style={lbl}>Effort <span style={{ color: C.muted, fontWeight: 400, textTransform: 'none' }}>(optional)</span></span>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                {[
                  { key: 'actual_duration_minutes', label: 'Duration (min)',      ph: 'e.g. 90',  min: 0,  max: 600 },
                  { key: 'avg_heart_rate',          label: 'Avg heart rate (bpm)', ph: 'e.g. 145', min: 30, max: 250 },
                  { key: 'calories',                label: 'Calories (kcal)',      ph: 'e.g. 800', min: 0,  max: 5000 },
                ].map(f => (
                  <div key={f.key} style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: C.text, fontSize: 11, fontWeight: 600, marginBottom: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.label}</div>
                    <input
                      type="number" min={f.min} max={f.max} inputMode="numeric"
                      placeholder={f.ph}
                      value={form[f.key]}
                      onChange={e => set(f.key, e.target.value)}
                      style={{ ...inp, background: 'rgba(255,255,255,0.13)', border: '1px solid rgba(255,255,255,0.28)' }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Post notes */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
                <span style={{ ...lbl, marginBottom: 0 }}>Post-session notes</span>
                {!readOnly && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MicButton authSession={authSession} field="post_notes" onText={t =>
                      set('post_notes', (form.post_notes ? `${form.post_notes} ${t}` : t).slice(0, 4000))
                    } />
                    <button type="button" onClick={cleanupPostNotes} disabled={!form.post_notes?.trim() || cleaningUp}
                      title="Fix grammar and filler words without changing what it says"
                      style={{
                        background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.35)',
                        borderRadius: 20, color: '#6ee7b7', fontSize: 11, fontWeight: 700,
                        padding: '4px 10px', cursor: (!form.post_notes?.trim() || cleaningUp) ? 'default' : 'pointer',
                        opacity: (!form.post_notes?.trim() || cleaningUp) ? 0.5 : 1, fontFamily: C.font,
                      }}>{cleaningUp ? '…cleaning up' : '✨ Clean up'}</button>
                  </span>
                )}
              </div>
              <textarea value={form.post_notes} onChange={e => set('post_notes', e.target.value.slice(0, 4000))}
                maxLength={4000}
                placeholder="How did it go? What worked? What to improve?"
                rows={6} style={{ ...inp, resize: 'vertical', minHeight: 120 }} />
              <CharCounter value={form.post_notes} max={4000} />
            </div>
          </div>
        )}

        {/* Save error — auto-scrolled into view (see effect above), never silent */}
        {saveError && (
          <div ref={saveErrorRef} style={{
            margin: '12px 0 0', padding: '10px 14px', borderRadius: 8,
            background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
            color: '#fca5a5', fontSize: 13,
          }}>
            ⚠️ {saveError}
          </div>
        )}

        {/* Actions — sticky so Save is always visible above mobile keyboard */}
        <div style={{
          position: 'sticky', bottom: 0,
          background: '#111827',
          paddingTop: 12, paddingBottom: 70, marginTop: 16,
          borderTop: `1px solid ${C.border}`,
          display: 'flex', gap: 8,
        }}>
          <button onClick={onClose} style={{
            padding: '11px 14px', borderRadius: 10,
            border: `1px solid ${C.border}`, background: 'transparent',
            color: C.muted, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: C.font,
          }}>← Back</button>
          {readOnly ? (
            <button onClick={onClose} style={{
              flex: 1, padding: '14px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: C.border, color: C.muted, fontWeight: 700, fontSize: 14, fontFamily: C.font,
              minHeight: 48,
            }}>Close</button>
          ) : (
            <>
              {!isNew && (
                <button onClick={handleDelete} style={{
                  padding: '11px 14px', borderRadius: 10,
                  border: `1px solid ${C.red}44`, background: `${C.red}10`,
                  color: C.red, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: C.font,
                }}>Delete</button>
              )}
              <button onClick={handleSave} disabled={saving} style={{
                flex: 1, padding: '14px', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: C.accent, color: '#fff', fontWeight: 700, fontSize: 14, fontFamily: C.font,
                minHeight: 48,
              }}>
                {saving ? 'Saving…' : isNew ? 'Add game plan' : 'Save changes'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>

    {prepNotesOpen && (
      <div onClick={e => { if (e.target === e.currentTarget) setPrepNotesOpen(false); }} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.80)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2200, padding: 20,
      }}>
        <div style={{
          background: '#161b22', border: '1.5px solid rgba(139,92,246,0.55)',
          borderRadius: 16, padding: 24, width: '100%', maxWidth: 440,
          maxHeight: '85vh', overflowY: 'auto', fontFamily: C.font,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>💬 Preparation notes by Data Coach</div>
            <button onClick={() => setPrepNotesOpen(false)} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
          </div>
          <div style={{ color: C.muted, fontSize: 12, marginBottom: 18 }}>
            Observations from your own match history — not a plan. You decide what, if anything, to do with them.
          </div>

          {prepNotesLoading && (
            <div style={{ color: C.muted, fontSize: 13, padding: '20px 0', textAlign: 'center' }}>Looking through your match history…</div>
          )}

          {prepNotesError && (
            <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '9px 12px', color: '#fca5a5', fontSize: 12.5 }}>
              {prepNotesError}
            </div>
          )}

          {prepNotesData && !prepNotesData.available && (
            <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, padding: '8px 0' }}>
              {prepNotesData.reason}
            </div>
          )}

          {prepNotesData?.available && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                {prepNotesData.notes.map((note, i) => (
                  <div key={i} style={{
                    background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.22)',
                    borderRadius: 10, padding: '11px 13px',
                  }}>
                    <div style={{ color: C.text, fontSize: 13, lineHeight: 1.55, marginBottom: 8 }}>{note}</div>
                    <button onClick={() => insertPrepNote(note)} style={{
                      background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.4)',
                      borderRadius: 6, color: '#c4b5fd', fontSize: 11.5, fontWeight: 600,
                      padding: '4px 10px', cursor: 'pointer', fontFamily: C.font,
                    }}>+ Add to "In the scope today"</button>
                  </div>
                ))}
              </div>
              {prepNotesData.based_on?.length > 0 && (
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                  <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Based on</div>
                  {prepNotesData.based_on.map((b, i) => (
                    <div key={i} style={{ color: C.muted, fontSize: 11.5, lineHeight: 1.6 }}>· {b}</div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )}
    </>
  );
}

// ── Auto-match popup ──────────────────────────────────────────────────────────

// ── Session pill ──────────────────────────────────────────────────────────────
// Tooltip rendered at fixed screen coordinates — does NOT affect cell layout
function SessionTooltip({ s, rc, typeColor, typeLabel, x, y }) {
  const resultLabel = {
    win: 'Win', loss: 'Loss', draw: 'Draw', achieved: 'Achieved', not_achieved: 'Not achieved',
  }[s.result];
  const stars = s.performance_score ? '★'.repeat(s.performance_score) + '☆'.repeat(5 - s.performance_score) : null;

  // Keep tooltip on screen — flip left if too close to right edge
  const left = Math.min(x + 12, window.innerWidth - 260);
  const top  = Math.max(y - 10, 8);

  return (
    <div style={{
      position: 'fixed', zIndex: 9999, left, top,
      background: '#1c2333', border: '1px solid rgba(255,255,255,0.18)',
      borderRadius: 10, padding: '10px 13px', width: 230,
      boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
      pointerEvents: 'none',
      fontFamily: C.font, fontSize: 12, color: C.text,
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7, borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 7 }}>
        <span style={{ fontSize: 15 }}>{sportEmoji(s.sport)}</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 12, color: typeColor }}>{typeLabel}</div>
          <div style={{ fontSize: 11, color: C.muted }}>
            {sportLabel(s.sport)}
          </div>
        </div>
        {resultLabel && (
          <span style={{
            marginLeft: 'auto', fontSize: 10, fontWeight: 700,
            color: rc, background: `${rc}20`, border: `1px solid ${rc}40`,
            borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap',
          }}>{resultLabel}</span>
        )}
      </div>
      {/* Details */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {s.scheduled_date && (
          <TipRow label="Date"     value={fmtDate(s.scheduled_date.slice(0, 10))} />
        )}
        {s.estimated_duration && (
          <TipRow label="Duration" value={`${s.estimated_duration} min`} />
        )}
        {(s.opponent_name || s.race_name) && (
          <TipRow label={s.type === 'race_day' ? 'Race' : 'vs'} value={s.opponent_name ? opponentLabel(s) : s.race_name} />
        )}
        {s.goal && (
          <TipRow label="Goal"  value={s.goal}       wrap />
        )}
        {stars && (
          <TipRow label="Perf." value={<span style={{ color: '#f59e0b', letterSpacing: 1 }}>{stars}</span>} />
        )}
        {s.post_notes && (
          <TipRow label="Notes" value={s.post_notes} wrap />
        )}
      </div>
    </div>
  );
}

function TipRow({ label, value, wrap }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: wrap ? 'flex-start' : 'center' }}>
      <span style={{ fontSize: 10, color: C.muted, minWidth: 46, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 11, color: C.text, lineHeight: 1.4,
        ...(wrap ? {} : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
      }}>{value}</span>
    </div>
  );
}

function SessionPill({ session: s, onEdit }) {
  const isMobile = window.innerWidth < 640;
  const [tipPos, setTipPos] = useState(null); // {x, y} or null
  const hasResult = !!s.result;
  const resultColors = {
    win: '#22c55e', loss: '#ef4444', draw: '#94a3b8',
    achieved: '#22c55e', not_achieved: '#f59e0b',
  };
  const rc = resultColors[s.result] || C.muted;

  const typeColor = {
    match:       '#ef4444',
    competition: '#f97316',
    race_day:    '#f97316',
    brick:       '#a78bfa',
    training:    '#3b82f6',
  }[s.type] || '#3b82f6';

  const typeLabel = {
    match:       'Match',
    competition: 'Race',
    race_day:    'Race',
    brick:       'Brick',
    training:    'Training',
  }[s.type] || s.type;

  const pillBg = hasResult ? `${rc}20` : `${typeColor}18`;
  const pillBorder = hasResult ? `${rc}55` : `${typeColor}40`;

  return (
    <div
      onClick={() => onEdit(s)}
      onMouseEnter={e => !isMobile && setTipPos({ x: e.clientX, y: e.clientY })}
      onMouseMove={e  => !isMobile && setTipPos({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => !isMobile && setTipPos(null)}
      style={{
        background: pillBg,
        border: `1px solid ${pillBorder}`,
        borderRadius: 8, padding: '5px 8px', cursor: 'pointer', marginBottom: 3,
      }}
    >
      {tipPos && (
        <SessionTooltip s={s} rc={rc} typeColor={typeColor} typeLabel={typeLabel}
          x={tipPos.x} y={tipPos.y} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ fontSize: 12 }}>{sportEmoji(s.sport)}</span>
        <span style={{ fontSize: 10, color: typeColor, fontWeight: 700, flex: 1 }}>
          {typeLabel}
        </span>
        {hasResult && (
          <span style={{ color: rc, fontSize: 10, fontWeight: 700 }}>
            {s.result === 'win' ? 'W' : s.result === 'loss' ? 'L' : s.result === 'draw' ? 'D' : s.result === 'achieved' ? '✓' : '⚠'}
          </span>
        )}
      </div>
      {s.goal && (
        <div style={{ fontSize: 10, color: C.muted, marginTop: 1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {s.goal}
        </div>
      )}
    </div>
  );
}

// ── Stats strip ───────────────────────────────────────────────────────────────
function StatsStrip({ plans }) {
  const isMobile = useIsMobile();
  const completed  = plans.filter(p => p.result);
  const wins       = plans.filter(p => ['win','achieved'].includes(p.result)).length;
  const winRate    = completed.length ? Math.round((wins / completed.length) * 100) : null;
  const avgPerf    = completed.filter(p => p.performance_score).length
    ? (completed.filter(p => p.performance_score)
        .reduce((a, b) => a + b.performance_score, 0)
       / completed.filter(p => p.performance_score).length).toFixed(1) : null;
  const avgAdh     = completed.filter(p => p.game_plan_adherence).length
    ? (completed.filter(p => p.game_plan_adherence)
        .reduce((a, b) => a + b.game_plan_adherence, 0)
       / completed.filter(p => p.game_plan_adherence).length).toFixed(1) : null;

  const stats = [
    { v: plans.length,     l: 'Game Plans',     c: C.accent  },
    { v: completed.length, l: 'Completed',    c: C.accent  },
    winRate !== null && { v: `${winRate}%`, l: 'Win/Achieved', c: '#22c55e' },
    avgPerf  && { v: `${avgPerf}★`,        l: 'Avg perf',     c: '#f59e0b' },
    avgAdh   && { v: `${avgAdh}★`,         l: 'Plan outcome', c: C.purple  },
  ].filter(Boolean);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))`,
      background: 'rgba(13,17,23,0.92)',
      border: `1px solid ${C.border}`,
      borderRadius: 12, padding: isMobile ? '10px 2px' : '14px 8px', marginBottom: 16,
    }}>
      {stats.map((s, i) => (
        <div key={s.l} style={{
          textAlign: 'center', minWidth: 0,
          borderRight: i < stats.length - 1 ? `1px solid ${C.border}` : 'none',
          padding: isMobile ? '0 2px' : '0 8px',
        }}>
          <div style={{ fontSize: isMobile ? 15 : 22, fontWeight: 800, color: s.c, lineHeight: 1.1 }}>{s.v}</div>
          <div style={{ fontSize: isMobile ? 8.5 : 10, color: C.muted, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.02em', overflowWrap: 'break-word' }}>{s.l}</div>
        </div>
      ))}
    </div>
  );
}


// ── Weekly Plan Table ────────────────────────────────────────────────────────


const FEELING_CHIPS_GP = [
  { emoji: '🔥', label: 'Paremmin kuin odotin', value: 1 },
  { emoji: '👍', label: 'Suunnitelman mukaan',  value: 2 },
  { emoji: '😅', label: 'Rankempaa kuin luulin', value: 3 },
  { emoji: '💀', label: 'Ei mennyt hyvin',       value: 4 },
];



// ── Pairing View (Game Plan tab) ─────────────────────────────────────────────

// ── Session Summary Section ───────────────────────────────────────────────────
function SessionSummarySection({ plans, today, onEdit, onNavigate, onRefresh, showUpcoming = true, showPast = true, pastFlushTop = false, pastWindowDays = null, matchOnly = false }) {
  const todayISO = toISO(today);
  const isMobile = useIsMobile();
  const [showPastModal,     setShowPastModal]     = useState(false);
  const [showUpcomingModal, setShowUpcomingModal] = useState(false);
  const [selectedIds,       setSelectedIds]       = useState(new Set());
  const [deleting,          setDeleting]          = useState(false);

  // Home ("Your Recent Form") is match analytics — training sessions (and any
  // non-racket sport they were logged under before training became opt-in)
  // are excluded there. The Game Plans tab keeps showing everything.
  const ALL_TYPES = matchOnly ? ['match','competition'] : ['training','match','competition','race_day','brick'];

  // When pastWindowDays is set (Home), scope to exactly the same window the
  // "Your Recent Form" chart plots, so every row below is one of the points above.
  const pastWindowStartISO = useMemo(() => {
    if (!pastWindowDays) return null;
    const d = new Date(today);
    d.setDate(d.getDate() - pastWindowDays);
    return toISO(d);
  }, [today, pastWindowDays]);

  const pastSessions = useMemo(() =>
    plans
      .filter(p => ALL_TYPES.includes(p.type) && (
        p.scheduled_date?.slice(0,10) < todayISO ||
        (p.result || p.performance_score)  // has result → always past
      ))
      .filter(p => !pastWindowStartISO || p.scheduled_date?.slice(0,10) >= pastWindowStartISO)
      .sort((a,b) => b.scheduled_date.localeCompare(a.scheduled_date)),
    [plans, todayISO, pastWindowStartISO] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const upcomingSessions = useMemo(() =>
    plans
      .filter(p => ALL_TYPES.includes(p.type) &&
        p.scheduled_date?.slice(0,10) >= todayISO &&
        !p.result && !p.performance_score  // no result yet
      )
      .sort((a,b) => a.scheduled_date.localeCompare(b.scheduled_date)),
    [plans, todayISO] // eslint-disable-line react-hooks/exhaustive-deps
  );

  function getStatus(p) {
    if (p.result || p.performance_score) return { label: 'Completed', color: '#10b981' };
    return                               { label: 'Scheduled', color: C.amber  };
  }

  function daysUntil(iso) {
    const diff = Math.round((new Date(iso + 'T12:00:00') - new Date(todayISO + 'T12:00:00')) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    return `in ${diff}d`;
  }

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleSelectAll(items) {
    const allIds = items.map(p => p.id);
    const allSelected = allIds.every(id => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(allIds));
  }
  async function deleteSelected() {
    if (selectedIds.size === 0) return;
    if (!(await confirmDialog(`Delete ${selectedIds.size} session${selectedIds.size !== 1 ? 's' : ''}? They are kept and can be restored from "Recently deleted".`, { confirmLabel: 'Delete' }))) return;
    setDeleting(true);
    const { error } = await supabase.from('session_plans').update({ deleted_at: new Date().toISOString() }).in('id', [...selectedIds]);
    setDeleting(false);
    if (error) { alert('Delete failed: ' + error.message); return; }
    setSelectedIds(new Set());
    if (onRefresh) onRefresh();
  }

  const TYPE_COLOR = { match: C.red, competition: '#f97316', race_day: '#f97316', brick: C.purple, training: C.muted };
  const TYPE_EN_LBL = { match: 'MATCH', competition: 'RACE', race_day: 'RACE', brick: 'BRICK', training: 'TRAINING' };

  // Both tables render as a CSS grid (header row and each data row share the
  // same gridTemplateColumns), so column widths are resolved once by the
  // browser's grid layout instead of by hand-computed flex pixel math —
  // the previous flex approach kept drifting out of sync between what the
  // columns were told to be and what their content actually needed,
  // alternating between a stray scrollbar and clipped-off columns. A grid
  // track can't do either: every column is always exactly as wide as its
  // template says, and minmax(0,1fr) on SESSION absorbs whatever space is
  // left (down to a sliver) instead of overflowing.
  const upcomingGridCols = isMobile
    ? '18px 46px minmax(0,1fr) 38px 52px'
    : '28px 64px minmax(0,1fr) 78px 62px 86px';
  const pastGridCols = isMobile
    ? '48px minmax(0,1fr) 42px 50px 62px'
    : '64px minmax(0,1fr) 78px 62px 68px 56px 84px';

  const col = (w, align = 'right') => ({ textAlign: align, padding: isMobile ? '0 2px' : '0 6px', minWidth: 0, overflow: 'hidden' });
  const hdr = { color: C.muted, fontSize: isMobile ? 9 : 10, fontWeight: 700, letterSpacing: '0.3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'clip' };

  // The player's own optional duration / avg HR entries for a plan
  function linkedAct(p) {
    if (!p.actual_duration_minutes && !p.avg_heart_rate && !p.calories) return null;
    return { duration_minutes: p.actual_duration_minutes, avg_hr: p.avg_heart_rate, calories: p.calories };
  }

  // Compact effort chip
  function ActChip({ act }) {
    if (!act) return null;
    const parts = [];
    if (act.duration_minutes) parts.push(`${act.duration_minutes}m`);
    if (act.avg_hr)           parts.push(`${act.avg_hr}bpm`);
    if (act.calories)         parts.push(`${act.calories}kcal`);
    if (!parts.length)        return null;
    return (
      <span style={{
        fontSize: 10, color: C.accent, background: `${C.accent}14`,
        border: `1px solid ${C.accent}35`, borderRadius: 4,
        padding: '1px 5px', marginLeft: 5, whiteSpace: 'nowrap', flexShrink: 0,
      }}>
        ⏱ {parts.join(' · ')}
      </span>
    );
  }

  /* ── Reusable table shell ── */
  function PlanTable({ title, icon, items, limit, onShowAll, colHeaders, children, actionButton, flushTop = false, bare = false, gridCols }) {
    const visible = items.slice(0, limit);
    return (
      <div style={bare ? { overflow: 'hidden', maxWidth: '100%' } : {
        background: 'rgba(10,14,20,0.85)',
        borderRadius: flushTop ? '0 0 14px 14px' : 14,
        border: `1px solid ${C.border}`,
        borderTop: flushTop ? '1px solid rgba(255,255,255,0.10)' : `1px solid ${C.border}`,
        marginBottom: 16, overflow: 'hidden', maxWidth: '100%',
      }}>
        {/* Title bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', borderBottom: `1px solid ${C.moduleBorder}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
            <h3 style={{ margin: 0, color: C.text, fontSize: 14, fontWeight: 800, letterSpacing: '-0.2px', whiteSpace: 'nowrap' }}>
              {icon} {title}
            </h3>
            <span style={{ fontSize: 12, color: C.muted, flexShrink: 0 }}>{items.length} plan{items.length !== 1 ? 's' : ''}</span>
          </div>
          {actionButton || (items.length > limit && (
            <button onClick={onShowAll} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 12, color: C.blue, fontFamily: C.font, padding: 0, fontWeight: 600, flexShrink: 0,
            }}>Show all →</button>
          ))}
        </div>
        {/* Table area — overflowX is a safety net for extreme widths only; the row
            itself must NOT be minWidth:'max-content', or the flexible SESSION
            column's ellipsis never triggers (the row just balloons to fit the
            longest text instead of truncating), giving every row a different
            width and making the table look inconsistent on mobile. */}
        {/* Columns are CSS-grid-sized to fit their container (see the
            upcomingGridCols/pastGridCols templates below), so this should
            never need to scroll — but overflow stays 'auto' as a safety
            net rather than 'hidden', because silently clipping real data
            off the edge is worse than an occasional scrollbar. */}
        <div style={{ overflowX: 'auto' }}>
          {/* Column header row — boxSizing:'border-box' is load-bearing here:
              without it, the grid's track-sizing algorithm was computing
              widths against the full 100% before the row's own horizontal
              padding was subtracted, so the row rendered ~28px (exactly
              the left+right padding) wider than its container and the
              last column clipped off the edge. */}
          <div style={{
            display: 'grid', gridTemplateColumns: gridCols, alignItems: 'center',
            padding: isMobile ? '6px 8px' : '7px 14px', borderBottom: `1px solid rgba(56,189,248,0.30)`,
            background: 'rgba(255,255,255,0.03)', width: '100%', boxSizing: 'border-box',
          }}>
            {colHeaders}
          </div>
          {/* Rows */}
          {visible.length === 0
            ? <div style={{ fontSize: 13, color: C.muted, padding: '18px 16px' }}>No game plans yet.</div>
            : <div style={{ width: '100%' }}>{children(visible)}</div>
          }
        </div>
      </div>
    );
  }

  /* ── Past row ── */
  function PastRow({ p }) {
    const st          = getStatus(p);
    const act         = linkedAct(p);
    const resultColor = { win:'#22c55e', loss:C.red, draw:'#eab308', achieved:'#22c55e', not_achieved:C.amber }[p.result] || null;
    const resultLabel = { win:'WIN', loss:'LOSS', draw:'DRAW', achieved:'DONE', not_achieved:'MISSED'  }[p.result] || '—';
    return (
      <div
        onClick={() => onEdit(p)}
        style={{
          display: 'grid', gridTemplateColumns: pastGridCols, alignItems: 'center',
          width: '100%', boxSizing: 'border-box',
          padding: isMobile ? '8px 8px' : '10px 14px', cursor: 'pointer', fontFamily: C.font,
          color: C.text,
          borderBottom: `1px solid rgba(255,255,255,0.05)`,
          transition: 'background 0.12s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      >
        {/* Date */}
        <div style={{ ...col(62, 'left') }}>
          <span style={{ fontSize: 12, color: C.text }}>{fmtDate(p.scheduled_date?.slice(0,10))}</span>
        </div>
        {/* Activity name + linked act chip */}
        <div style={{ paddingLeft: 4, minWidth: 0, display: 'flex', alignItems: 'center', gap: 7, overflow: 'hidden' }}>
          <span style={{ fontSize: 17, flexShrink: 0 }}>{sportEmoji(p.sport)}</span>
          <span style={{ fontSize: 13, color: C.text, fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {p.goal || sportLabel(p.sport)}
          </span>
          {p.opponent_name && !isMobile && (
            <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>vs {opponentLabel(p)}</span>
          )}
          {act && <ActChip act={act} />}
        </div>
        {/* Type */}
        {!isMobile && (
          <div style={{ ...col(76) }}>
            <span style={{
              fontSize: 10, fontWeight: 700,
              color: TYPE_COLOR[p.type] || C.muted,
              background: 'rgba(255,255,255,0.08)', borderRadius: 4, padding: '2px 6px',
            }}>{TYPE_EN_LBL[p.type] || (p.type || '').replace('_',' ').toUpperCase()}</span>
          </div>
        )}
        {/* Planned duration */}
        <div style={{ ...col(60) }}>
          <span style={{ fontSize: 12, color: C.text }}>
            {p.estimated_duration ? `${p.estimated_duration}m` : '—'}
          </span>
        </div>
        {/* Result */}
        <div style={{ ...col(66) }}>
          {p.result
            ? <span style={{ fontSize: 11, fontWeight: 700, color: resultColor || C.text }}>{resultLabel}</span>
            : <span style={{ fontSize: 12, color: C.text }}>—</span>
          }
        </div>
        {/* Stars */}
        {!isMobile && (
          <div style={{ ...col(54) }}>
            <span style={{ fontSize: 11, color: '#f59e0b', letterSpacing: 1 }}>
              {p.performance_score > 0
                ? '★'.repeat(p.performance_score) + '☆'.repeat(5 - p.performance_score)
                : <span style={{ color: C.dim }}>—</span>
              }
            </span>
          </div>
        )}
        {/* Status badge */}
        <div style={{ ...col(82) }}>
          <span style={{
            fontSize: 10, fontWeight: 700, color: st.color,
            background: `${st.color}22`, borderRadius: 4, padding: '2px 7px',
          }}>{st.label}</span>
        </div>
      </div>
    );
  }

  /* ── Upcoming row ── */
  function UpcomingRow({ p }) {
    const due      = daysUntil(p.scheduled_date?.slice(0,10));
    const dueCl    = due === 'Today' ? C.accent : due === 'Tomorrow' ? C.amber : C.blue;
    const selected = selectedIds.has(p.id);
    return (
      <div
        role="button" tabIndex={0} onClick={() => onEdit(p)} onKeyDown={onEnterOrSpace(() => onEdit(p))}
        style={{
          display: 'grid', gridTemplateColumns: upcomingGridCols, alignItems: 'center',
          width: '100%', boxSizing: 'border-box',
          padding: isMobile ? '8px 8px' : '10px 14px', fontFamily: C.font,
          color: C.text,
          borderBottom: `1px solid rgba(255,255,255,0.05)`,
          background: selected ? 'rgba(59,130,246,0.10)' : 'transparent',
          transition: 'background 0.12s', cursor: 'pointer',
        }}
        onMouseEnter={e => { if (!selected) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
        onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Checkbox — the row above already opens the edit form on click/
            Enter/Space, so this one control that does something different
            still needs its own stopPropagation */}
        <div onClick={e => e.stopPropagation()}>
          <input type="checkbox" checked={selected}
            onChange={() => toggleSelect(p.id)}
            style={{ width: 15, height: 15, accentColor: C.blue, cursor: 'pointer' }} />
        </div>
        {/* Date */}
        <div style={col(62, 'left')}>
          <span style={{ fontSize: 12, color: C.text }}>{fmtDate(p.scheduled_date?.slice(0,10))}</span>
        </div>
        {/* Activity name */}
        <div style={{ paddingLeft: 4, minWidth: 0, display: 'flex', alignItems: 'center', gap: isMobile ? 5 : 7 }}>
          <span style={{ fontSize: isMobile ? 15 : 17, flexShrink: 0 }}>{sportEmoji(p.sport)}</span>
          <span style={{ fontSize: isMobile ? 12 : 13, color: C.text, fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {p.goal || sportLabel(p.sport)}
          </span>
          {p.opponent_name && !isMobile && (
            <span style={{ fontSize: 11, color: C.text, flexShrink: 0 }}>vs {opponentLabel(p)}</span>
          )}
        </div>
        {/* Type */}
        {!isMobile && (
          <div style={{ ...col(76) }}>
            <span style={{
              fontSize: 10, fontWeight: 700,
              color: TYPE_COLOR[p.type] || C.muted,
              background: 'rgba(255,255,255,0.08)', borderRadius: 4, padding: '2px 6px',
            }}>{TYPE_EN_LBL[p.type] || (p.type || '').replace('_',' ').toUpperCase()}</span>
          </div>
        )}
        {/* Planned duration */}
        <div style={{ ...col(60) }}>
          <span style={{ fontSize: isMobile ? 11 : 12, color: C.text }}>
            {p.estimated_duration ? `${p.estimated_duration}m` : '—'}
          </span>
        </div>
        {/* Countdown */}
        <div style={{ ...col(84) }}>
          <span style={{
            fontSize: isMobile ? 10 : 11, fontWeight: 700, color: dueCl,
            background: `${dueCl}22`, borderRadius: 5, padding: isMobile ? '2px 5px' : '2px 8px',
          }}>{due}</span>
        </div>
      </div>
    );
  }

  /* ── Expanded modal ── */
  function SummaryModal({ title, items, renderRow, onClose }) {
    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9000, padding: 16,
      }} onClick={onClose}>
        <div style={{
          background: '#161b22', borderRadius: 16, width: '100%', maxWidth: 560,
          maxHeight: '80vh', display: 'flex', flexDirection: 'column',
          border: '1.5px solid rgba(56,189,248,0.65)',
        }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px', borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontWeight: 700, color: C.text, fontSize: 14 }}>{title}</span>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: C.muted, fontSize: 18, fontFamily: C.font, lineHeight: 1,
            }}>✕</button>
          </div>
          <div style={{ overflowY: 'auto', padding: '4px 0' }}>
            {items.slice(0, 20).map(p => renderRow(p))}
          </div>
        </div>
      </div>
    );
  }

  /* ── Column header sets ── */
  const pastHeaders = (
    <>
      <div style={{ ...col(62, 'left'), ...hdr }}>DATE</div>
      <div style={{ minWidth: 0, paddingLeft: 4, ...hdr }}>SESSION</div>
      {!isMobile && <div style={{ ...col(76), ...hdr }}>TYPE</div>}
      <div style={{ ...col(60), ...hdr }}>{isMobile ? 'DUR' : 'PLAN DUR'}</div>
      <div style={{ ...col(66), ...hdr }}>RESULT</div>
      {!isMobile && <div style={{ ...col(54), ...hdr }}>PERF</div>}
      <div style={{ ...col(82), ...hdr }}>STATUS</div>
    </>
  );

  const allUpcomingVisible = upcomingSessions.slice(0, 5);
  const allUpcomingSelectedInView = allUpcomingVisible.length > 0 && allUpcomingVisible.every(p => selectedIds.has(p.id));

  const upcomingHeaders = (
    <>
      <div>
        <input type="checkbox"
          checked={allUpcomingSelectedInView}
          onChange={() => toggleSelectAll(allUpcomingVisible)}
          style={{ width: 15, height: 15, accentColor: C.blue, cursor: 'pointer' }} />
      </div>
      <div style={{ ...col(62, 'left'), ...hdr }}>DATE</div>
      <div style={{ minWidth: 0, paddingLeft: 4, ...hdr }}>SESSION</div>
      {!isMobile && <div style={{ ...col(76), ...hdr }}>TYPE</div>}
      <div style={{ ...col(60), ...hdr }}>{isMobile ? 'DUR' : 'PLAN DUR'}</div>
      <div style={{ ...col(84), ...hdr }}>{isMobile ? 'DUE' : 'DUE IN'}</div>
    </>
  );

  // Single shared card for both sections — see
  // docs_zenanalytics/09-CREATE-AND-MANAGE-REDESIGN-2026-09-29.md §3.7:
  // Upcoming and Past used to be two separately-bordered PlanTables that
  // read as unrelated floating boxes. One outer card, two internal
  // sections with a divider between them, keeps them visually one unit
  // while still clearly labeled and separable.
  return (
    <div style={{
      background: 'rgba(10,14,20,0.85)', borderRadius: 14,
      border: `1px solid ${C.border}`, marginBottom: 24, overflow: 'hidden', maxWidth: '100%',
    }}>
      {showUpcoming && (
        <PlanTable
          bare
          title="UPCOMING GAME PLANS"
          icon="📅"
          items={upcomingSessions}
          limit={5}
          onShowAll={() => setShowUpcomingModal(true)}
          colHeaders={upcomingHeaders}
          gridCols={upcomingGridCols}
          actionButton={
            selectedIds.size > 0
              ? (
                <button onClick={deleteSelected} disabled={deleting} style={{
                  background: 'rgba(239,68,68,0.15)', border: `1px solid ${C.red}60`,
                  color: C.red, borderRadius: 8, padding: '4px 12px',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: C.font,
                }}>
                  {deleting ? 'Deleting…' : `🗑 Delete (${selectedIds.size})`}
                </button>
              )
              : onNavigate
                ? <button onClick={onNavigate} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 12, color: C.blue, fontFamily: C.font, padding: 0, fontWeight: 600,
                  }}>Open Game Plan →</button>
                : null
          }
        >
          {visible => visible.map(p => <UpcomingRow key={p.id} p={p} />)}
        </PlanTable>
      )}

      {/* Divider between the two sections of the shared card — only when
          Upcoming actually rendered above it. */}
      {showUpcoming && showPast && <div style={{ height: 1, background: 'rgba(255,255,255,0.10)' }} />}

      {showPast ? (
        <PlanTable
          bare
          title="PAST GAME PLANS"
          icon="📋"
          items={pastSessions}
          limit={5}
          onShowAll={() => setShowPastModal(true)}
          colHeaders={pastHeaders}
          gridCols={pastGridCols}
        >
          {visible => visible.map(p => <PastRow key={p.id} p={p} />)}
        </PlanTable>
      ) : pastSessions.length > 0 && (
        // Past game plans are hidden here to keep this module compact — see
        // docs_zenanalytics/11-MODULE-CENTRIC-REDESIGN-2026-09-29.md. Still
        // reachable via the same modal, and always visible from Home.
        <div style={{
          padding: showUpcoming ? '8px 14px 10px' : '2px 0 0',
          borderTop: showUpcoming ? `1px solid rgba(255,255,255,0.10)` : 'none',
        }}>
          <button onClick={() => setShowPastModal(true)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            fontSize: 12, color: C.blue, fontFamily: C.font, fontWeight: 600,
          }}>📋 See past game plans ({pastSessions.length}) →</button>
        </div>
      )}

      {showUpcomingModal && (
        <SummaryModal
          title={`Upcoming Game Plans (${Math.min(upcomingSessions.length, 20)} / ${upcomingSessions.length})`}
          items={upcomingSessions}
          renderRow={p => <UpcomingRow key={p.id} p={p} />}
          onClose={() => setShowUpcomingModal(false)}
        />
      )}
      {showPastModal && (
        <SummaryModal
          title={`Past Game Plans (${Math.min(pastSessions.length, 20)} / ${pastSessions.length})`}
          items={pastSessions}
          renderRow={p => <PastRow key={p.id} p={p} />}
          onClose={() => setShowPastModal(false)}
        />
      )}
    </div>
  );
}

// ── Competition management ──────────────────────────────────────────────────
// Practice dropped as a type (2026-11-01) -- it never got its own treatment
// anywhere (CompetitionsSplitTable only buckets league/tournament, so a
// practice-type row silently fell into neither), and no account had ever
// used it. League and Tournament are the two shapes worth doing well.
const COMPETITION_TYPES = [
  { value: 'league',     label: '🏅 League',     hint: 'Recurring rounds over a season' },
  { value: 'tournament', label: '🏆 Tournament',  hint: 'A single bounded event' },
];

const BRACKET_FORMATS = [
  { value: 'single_elim',        label: 'Single elimination' },
  { value: 'double_elim',        label: 'Double elimination' },
  { value: 'round_robin_groups', label: 'Round-robin groups' },
  { value: 'swiss',              label: 'Swiss' },
];

function competitionIcon(type) {
  return type === 'league' ? '🏅' : type === 'tournament' ? '🏆' : '🎯';
}

function CompetitionFormModal({ competition, userId, activeSport, authSession, onSaved, onClose }) {
  const isNew = !competition?.id;
  // Plan / Result — same split as the session modal: plan it going in, review
  // how it actually went once it's over. Result only makes sense for an
  // existing competition, so a brand-new one starts (and stays) on Plan.
  const [tab,        setTab]        = useState('plan'); // 'plan' | 'result'
  const [name,       setName]       = useState(competition?.name || '');
  const [type,       setType]       = useState(competition?.type || 'league');
  const [startDate,  setStartDate]  = useState(competition?.start_date?.slice(0,10) || '');
  const [endDate,    setEndDate]    = useState(competition?.end_date?.slice(0,10) || '');
  const [location,   setLocation]   = useState(competition?.location || '');
  const [goal,       setGoal]       = useState(competition?.goal || '');
  const [strategy,   setStrategy]   = useState(competition?.format_meta?.strategy || '');
  const [totalMatchesPlanned, setTotalMatchesPlanned] = useState(competition?.format_meta?.total_matches_planned ?? '');
  const [bracketFormat,       setBracketFormat]       = useState(competition?.format_meta?.bracket_format || 'single_elim');
  const [expectedRoundsText,  setExpectedRoundsText]  = useState((competition?.format_meta?.expected_rounds || []).join(', '));
  const [reflection, setReflection] = useState(competition?.format_meta?.reflection || '');
  const [goalStatus,  setGoalStatusLocal] = useState(competition?.goal_status || null);
  // Pre-competition self-check — how ready the athlete feels going into the
  // WHOLE competition, same 1-5★ idea as a single session's mental/physical
  // readiness but scoped once per competition, not per match. Stored in
  // format_meta like the other Plan-side extras (strategy, bracket format)
  // rather than a new column — see PRODUCT-GUIDELINES.md §3 for why match-
  // level readiness is kept separate from Competition review's "Mental
  // focus" (a reflection after the fact, not a starting-state self-check).
  const [mentalReadiness,   setMentalReadiness]   = useState(competition?.format_meta?.mental_readiness ?? null);
  const [physicalReadiness, setPhysicalReadiness] = useState(competition?.format_meta?.physical_readiness ?? null);

  // "Preparation notes by Data Coach" for the whole competition — same
  // observation-only principle as the match-level version in SessionModal,
  // one hierarchy level up. Only meaningful for an existing competition
  // (needs a competition_id), same restriction as the Result tab above.
  const [prepNotesOpen,    setPrepNotesOpen]    = useState(false);
  const [prepNotesLoading, setPrepNotesLoading] = useState(false);
  const [prepNotesData,    setPrepNotesData]    = useState(null);
  const [prepNotesError,   setPrepNotesError]   = useState(null);

  useEffect(() => {
    if (!prepNotesOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setPrepNotesOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prepNotesOpen]);

  const fetchCompetitionPrepNotes = async () => {
    if (!competition?.id) return;
    setPrepNotesOpen(true);
    setPrepNotesLoading(true);
    setPrepNotesError(null);
    setPrepNotesData(null);
    try {
      const token = authSession?.access_token;
      const res = await fetch(`${API}/game-plans/competition-prep-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ competition_id: competition.id, sport: competition.sport || activeSport }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setPrepNotesData(await res.json());
    } catch (e) {
      setPrepNotesError("Couldn't load preparation notes right now — try again in a moment.");
    }
    setPrepNotesLoading(false);
  };

  // Same sourcing convention as the match-level insertPrepNote — appended as
  // clearly-attributed raw material, never silently merged into the
  // athlete's own words.
  const insertCompetitionPrepNote = (note) => {
    const line = `Data Coach: ${note}`;
    setStrategy(s => (s ? `${s}\n${line}` : line).slice(0, 600));
  };
  // Holistic review of the competition as a whole — distinct from the
  // auto-computed per-match averages already shown in CompetitionDetail's
  // season summary. Those are objective averages of logged matches; this is
  // the athlete's own reflective judgment now that it's over, on dimensions
  // that don't necessarily map to a single match stat (e.g. mental focus
  // across a whole season). See docs_zenanalytics/08-GAME-PLAN-LOGIC-REWRITE-2026-09-27.md §4.
  const [review, setReview] = useState(competition?.format_meta?.review || {});
  const setReviewField = (key, v) => setReview(r => ({ ...r, [key]: r[key] === v ? 0 : v }));
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);

  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required.'); return; }
    // A competition with sport=null silently drops out of every
    // sport-filtered view (Game Stats, Home, this very list) even though
    // the row is intact — found 2026-10-25 on a real account where
    // activeSport was still null (profile fetch hadn't resolved yet) at
    // the moment a brand-new competition was saved. Block rather than
    // save something that'll look "lost".
    if (isNew && !competition?.sport && !activeSport) {
      setError('Still loading your sport — wait a moment and try again.');
      return;
    }
    setSaving(true);
    setError(null);
    // "strategy"/"reflection" apply the same way regardless of type (league/
    // tournament/practice), unlike total_matches_planned/expected_rounds
    // which are type-specific — see
    // docs_zenanalytics/08-GAME-PLAN-LOGIC-REWRITE-2026-09-27.md §4.2.
    let format_meta = {};
    if (strategy.trim()) format_meta.strategy = strategy.trim();
    if (reflection.trim()) format_meta.reflection = reflection.trim();
    if (mentalReadiness)   format_meta.mental_readiness = mentalReadiness;
    if (physicalReadiness) format_meta.physical_readiness = physicalReadiness;
    const reviewClean = Object.fromEntries(Object.entries(review).filter(([, v]) => v > 0));
    if (Object.keys(reviewClean).length > 0) format_meta.review = reviewClean;
    if (type === 'league' && totalMatchesPlanned !== '') {
      format_meta = { ...format_meta, total_matches_planned: Number(totalMatchesPlanned) || null };
    } else if (type === 'tournament') {
      const rounds = expectedRoundsText.split(',').map(s => s.trim()).filter(Boolean);
      format_meta = { ...format_meta, bracket_format: bracketFormat, expected_rounds: rounds };
    }
    const payload = {
      user_id:    userId,
      // A competition's sport is set once, at creation, from whichever
      // sport was active — not editable afterward from this form (see
      // PRODUCT-GUIDELINES.md §5). Editing an existing one keeps its sport.
      ...(isNew ? { sport: activeSport || competition?.sport || null } : {}),
      name:       name.trim(),
      type,
      start_date: startDate || null,
      end_date:   endDate || null,
      location:   location.trim() || null,
      goal:       goal.trim() || null,
      goal_status: goalStatus,
      format_meta: Object.keys(format_meta).length > 0 ? format_meta : null,
    };
    try {
      const { error: dbErr } = isNew
        ? await supabase.from('competitions').insert(payload)
        : await supabase.from('competitions').update(payload).eq('id', competition.id);
      if (dbErr) throw dbErr;
      onSaved();
    } catch (e) {
      setError(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const inp = {
    width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.09)',
    border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 12px',
    color: C.text, fontSize: 13, fontFamily: C.font, outline: 'none',
  };
  const lbl = { display: 'block', color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 };

  useEscapeToClose(onClose);

  return (
    <>
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.80)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2100, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#161b22', border: '1.5px solid rgba(56,189,248,0.65)',
        borderRadius: 16, padding: 24, width: '100%', maxWidth: 420,
        maxHeight: '85vh', overflowY: 'auto', fontFamily: C.font,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>
            {isNew ? 'New competition' : 'Edit competition'}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '9px 12px', color: '#fca5a5', fontSize: 12.5, marginBottom: 14 }}>
            {error}
          </div>
        )}

        {!isNew && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            {[{ key: 'plan', label: '📋 Plan' }, { key: 'result', label: '📊 Result' }].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                flex: 1, background: tab === t.key ? C.accent : 'rgba(255,255,255,0.06)',
                color: tab === t.key ? '#04150f' : C.muted, border: 'none', borderRadius: 8,
                padding: '9px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: C.font,
              }}>{t.label}</button>
            ))}
          </div>
        )}

        {tab === 'result' && !isNew ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <span style={lbl}>Goal verdict</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setGoalStatusLocal(goalStatus === 'achieved' ? null : 'achieved')} style={{
                  flex: 1, background: goalStatus === 'achieved' ? 'rgba(34,197,94,0.18)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${goalStatus === 'achieved' ? '#22c55e' : C.border}`,
                  color: goalStatus === 'achieved' ? '#22c55e' : C.muted,
                  borderRadius: 8, padding: '9px 0', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: C.font,
                }}>✓ Achieved</button>
                <button onClick={() => setGoalStatusLocal(goalStatus === 'not_achieved' ? null : 'not_achieved')} style={{
                  flex: 1, background: goalStatus === 'not_achieved' ? 'rgba(239,68,68,0.18)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${goalStatus === 'not_achieved' ? '#ef4444' : C.border}`,
                  color: goalStatus === 'not_achieved' ? '#ef4444' : C.muted,
                  borderRadius: 8, padding: '9px 0', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: C.font,
                }}>✗ Not achieved</button>
              </div>
            </div>

            <div>
              <span style={lbl}>Rate how it went</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
                {[
                  { key: 'performance',         label: '💪 Performance' },
                  { key: 'plan_outcome',        label: '📋 Plan outcome' },
                  { key: 'tactical_execution',  label: '🎯 Tactical execution' },
                  { key: 'mental_focus',        label: '🧠 Mental focus' },
                ].map(({ key, label }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <span style={{ color: C.muted, fontSize: 12.5 }}>{label}</span>
                    <StarRating value={review[key] || 0} onChange={v => setReviewField(key, v)} />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ ...lbl, marginBottom: 0 }}>How did it go? (optional)</span>
                <MicButton authSession={authSession} field="competition_reflection" onText={t =>
                  setReflection(r => (r ? `${r} ${t}` : t).slice(0, 2000))
                } />
              </div>
              <textarea value={reflection} onChange={e => setReflection(e.target.value)}
                placeholder="Reflect now it's over — what worked, what didn't, what to carry into the next one…"
                style={{ ...inp, minHeight: 100, resize: 'vertical', fontFamily: C.font }} />
            </div>

            <button onClick={handleSave} disabled={saving} style={{
              background: C.accent, border: 'none', borderRadius: 8, padding: '11px 0',
              color: '#04150f', fontWeight: 700, fontSize: 13.5, cursor: saving ? 'default' : 'pointer',
              opacity: saving ? 0.7 : 1, fontFamily: C.font,
            }}>{saving ? 'Saving…' : 'Save changes'}</button>
          </div>
        ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <span style={lbl}>Name</span>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Kirte Cup Syksy 2026" style={inp} autoFocus />
          </div>

          <div>
            <span style={lbl}>Type</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {COMPETITION_TYPES.map(t => (
                <button key={t.value} onClick={() => setType(t.value)} title={t.hint} style={{
                  background: type === t.value ? 'rgba(16,185,129,0.18)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${type === t.value ? C.accent : C.border}`,
                  color: type === t.value ? C.accent : C.muted,
                  borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: 600,
                  cursor: 'pointer', fontFamily: C.font,
                }}>{t.label}</button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <span style={lbl}>Start date</span>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ ...inp, colorScheme: 'dark' }} />
            </div>
            <div style={{ flex: 1 }}>
              <span style={lbl}>End date</span>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ ...inp, colorScheme: 'dark' }} />
            </div>
          </div>

          <div>
            <span style={lbl}>Location (optional)</span>
            <input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Olarin Tennishalli" style={inp} />
          </div>

          {type === 'league' && (
            <div>
              <span style={lbl}>Matches planned this season (optional)</span>
              <input type="number" min="0" value={totalMatchesPlanned}
                onChange={e => setTotalMatchesPlanned(e.target.value)}
                placeholder="e.g. 12" style={inp} />
              <div style={{ color: C.dim, fontSize: 11, marginTop: 4 }}>
                Lets ZenAnalytics show how far through the season you are.
              </div>
            </div>
          )}

          {type === 'tournament' && (
            <>
              <div>
                <span style={lbl}>Bracket format</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {BRACKET_FORMATS.map(f => (
                    <button key={f.value} onClick={() => setBracketFormat(f.value)} style={{
                      background: bracketFormat === f.value ? 'rgba(16,185,129,0.18)' : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${bracketFormat === f.value ? C.accent : C.border}`,
                      color: bracketFormat === f.value ? C.accent : C.muted,
                      borderRadius: 8, padding: '6px 11px', fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', fontFamily: C.font,
                    }}>{f.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <span style={lbl}>Expected rounds (optional)</span>
                <input value={expectedRoundsText} onChange={e => setExpectedRoundsText(e.target.value)}
                  placeholder="e.g. Round 1, QF, SF, Final" style={inp} />
                <div style={{ color: C.dim, fontSize: 11, marginTop: 4 }}>
                  Comma-separated, in order. Used to show what's next even before you've scheduled it.
                </div>
              </div>
            </>
          )}

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ ...lbl, marginBottom: 0 }}>Competition goal (optional)</span>
              <MicButton authSession={authSession} field="competition_goal" onText={t =>
                setGoal(g => (g ? `${g} ${t}` : t).slice(0, 2000))
              } />
            </div>
            <textarea value={goal} onChange={e => setGoal(e.target.value)} placeholder="What does winning/succeeding here look like?"
              style={{ ...inp, minHeight: 64, resize: 'vertical', fontFamily: C.font }} />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ ...lbl, marginBottom: 0 }}>Competition game plan (optional)</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <MicButton authSession={authSession} field="competition_strategy" onText={t =>
                  setStrategy(s => (s ? `${s} ${t}` : t).slice(0, 2000))
                } />
                {!isNew && (
                  <button type="button" onClick={fetchCompetitionPrepNotes} style={{
                    background: 'rgba(139,92,246,0.10)', border: '1px solid rgba(139,92,246,0.35)',
                    borderRadius: 7, color: '#c4b5fd', fontSize: 11, fontWeight: 600,
                    padding: '4px 9px', cursor: 'pointer', fontFamily: C.font, whiteSpace: 'nowrap',
                  }}>💬 Preparation notes by Data Coach</button>
                )}
              </span>
            </div>
            <textarea value={strategy} onChange={e => setStrategy(e.target.value)}
              placeholder="Your approach — e.g. Physical league, protect energy in early rounds, go for broke in the final two matches…"
              style={{ ...inp, minHeight: 64, resize: 'vertical', fontFamily: C.font }} />
          </div>

          {/* A league has no single "going in" moment to rate readiness for
              -- it's relevant only for a bounded event you prepare for once,
              i.e. a tournament. Feedback 2026-11-01. */}
          {type === 'tournament' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <span style={lbl}>Mental readiness <span style={{ color: C.muted, fontWeight: 400, textTransform: 'none' }}>(optional)</span></span>
                <div style={{ marginTop: 4 }}>
                  <StarRating value={mentalReadiness} onChange={setMentalReadiness} />
                </div>
              </div>
              <div>
                <span style={lbl}>Physical readiness <span style={{ color: C.muted, fontWeight: 400, textTransform: 'none' }}>(optional)</span></span>
                <div style={{ marginTop: 4 }}>
                  <StarRating value={physicalReadiness} onChange={setPhysicalReadiness} />
                </div>
              </div>
            </div>
          )}

          <button onClick={handleSave} disabled={saving} style={{
            background: C.accent, border: 'none', borderRadius: 8, padding: '11px 0',
            color: '#04150f', fontWeight: 700, fontSize: 13.5, cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.7 : 1, fontFamily: C.font,
          }}>{saving ? 'Saving…' : (isNew ? 'Create competition' : 'Save changes')}</button>
        </div>
        )}
      </div>
    </div>

    {prepNotesOpen && (
      <div onClick={e => { if (e.target === e.currentTarget) setPrepNotesOpen(false); }} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.80)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2200, padding: 20,
      }}>
        <div style={{
          background: '#161b22', border: '1.5px solid rgba(139,92,246,0.55)',
          borderRadius: 16, padding: 24, width: '100%', maxWidth: 440,
          maxHeight: '85vh', overflowY: 'auto', fontFamily: C.font,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>💬 Preparation notes by Data Coach</div>
            <button onClick={() => setPrepNotesOpen(false)} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
          </div>
          <div style={{ color: C.muted, fontSize: 12, marginBottom: 18 }}>
            Observations from your own match history for this competition — not a plan. You decide what, if anything, to do with them.
          </div>

          {prepNotesLoading && (
            <div style={{ color: C.muted, fontSize: 13, padding: '20px 0', textAlign: 'center' }}>Looking through your match history…</div>
          )}

          {prepNotesError && (
            <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '9px 12px', color: '#fca5a5', fontSize: 12.5 }}>
              {prepNotesError}
            </div>
          )}

          {prepNotesData && !prepNotesData.available && (
            <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, padding: '8px 0' }}>
              {prepNotesData.reason}
            </div>
          )}

          {prepNotesData?.available && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                {prepNotesData.notes.map((note, i) => (
                  <div key={i} style={{
                    background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.22)',
                    borderRadius: 10, padding: '11px 13px',
                  }}>
                    <div style={{ color: C.text, fontSize: 13, lineHeight: 1.55, marginBottom: 8 }}>{note}</div>
                    <button onClick={() => insertCompetitionPrepNote(note)} style={{
                      background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.4)',
                      borderRadius: 6, color: '#c4b5fd', fontSize: 11.5, fontWeight: 600,
                      padding: '4px 10px', cursor: 'pointer', fontFamily: C.font,
                    }}>+ Add to "Competition game plan"</button>
                  </div>
                ))}
              </div>
              {prepNotesData.based_on?.length > 0 && (
                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                  <div style={{ color: C.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Based on</div>
                  {prepNotesData.based_on.map((b, i) => (
                    <div key={i} style={{ color: C.muted, fontSize: 11.5, lineHeight: 1.6 }}>· {b}</div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )}
    </>
  );
}

// ── Season Goal + Milestones management ───────────────────────────────────────
// See docs_zenanalytics/10-SEASON-GOALS-MILESTONES-2026-09-29.md. A season
// goal is a timeboxed goal (same shape as a competition — title/goal/dates/
// status); milestones belong to it and are evaluated with the same active/
// achieved/not_achieved verdict pattern competitions already use, optionally
// tagged to a specific competition ("top-3 in KirteCup") or standing alone
// ("improve first-serve consistency").
const MILESTONE_TYPES = [
  { value: 'result', label: '🏆 Result',  hint: 'Placement, wins, a scoreline' },
  { value: 'skill',  label: '🎯 Skill',   hint: 'Serve, footwork, timing, technique' },
  { value: 'other',  label: '📌 Other',   hint: 'Anything else worth tracking' },
];

function SeasonGoalManagerModal({ userId, seasonGoal, milestones, competitions, activeSport, onRefresh, onClose }) {
  // "Season goal" used to conflate the time period itself (a season — which
  // can span several leagues/tournaments) with the aspirational target
  // within it. `name`/`description` describe what the season actually IS;
  // `goal` stays what you're aiming for inside it. See
  // docs_zenanalytics/12-SEASON-REDESIGN-2026-10-01.md.
  const [name,      setName]      = useState(seasonGoal?.name || '');
  const [description, setDescription] = useState(seasonGoal?.description || '');
  const [goalText,  setGoalText]  = useState(seasonGoal?.goal || '');
  const [startDate, setStartDate] = useState(seasonGoal?.start_date?.slice(0, 10) || '');
  const [endDate,   setEndDate]   = useState(seasonGoal?.end_date?.slice(0, 10) || '');
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState(null);
  const [linkingCompId, setLinkingCompId] = useState(null);

  const saveGoal = async () => {
    // Same race condition as CompetitionFormModal: a brand-new season goal
    // saved before the profile's primary sport has loaded gets sport=null
    // and silently drops out of every sport-filtered view afterward.
    if (!seasonGoal?.id && !activeSport) {
      setError('Still loading your sport — wait a moment and try again.');
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      user_id: userId, title: 'Season goal',
      // Same rule as competitions — a Season's sport is set once at
      // creation from whichever sport is active, never edited from this
      // form afterward. See PRODUCT-GUIDELINES.md §5.
      ...(seasonGoal?.id ? {} : { sport: activeSport || null }),
      name: name.trim() || null,
      description: description.trim() || null,
      goal: goalText.trim() || null,
      start_date: startDate || null, end_date: endDate || null,
      status: 'active',
    };
    try {
      const { error: dbErr } = seasonGoal?.id
        ? await supabase.from('season_goals').update(payload).eq('id', seasonGoal.id)
        : await supabase.from('season_goals').insert(payload);
      if (dbErr) throw dbErr;
      await onRefresh();
    } catch (e) {
      setError(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // Tag/untag a competition as part of this season — same optional-link
  // pattern as a milestone's competition_id, just the other direction.
  const toggleCompetitionInSeason = async (comp) => {
    if (!seasonGoal?.id) return;
    setLinkingCompId(comp.id);
    const nextSeasonId = comp.season_goal_id === seasonGoal.id ? null : seasonGoal.id;
    await supabase.from('competitions').update({ season_goal_id: nextSeasonId }).eq('id', comp.id);
    await onRefresh();
    setLinkingCompId(null);
  };

  const archiveGoal = async () => {
    if (!seasonGoal?.id) return;
    await supabase.from('season_goals').update({ status: 'archived' }).eq('id', seasonGoal.id);
    await onRefresh();
    onClose();
  };

  // ── Milestones ──
  const [newTitle,  setNewTitle]  = useState('');
  const [newType,   setNewType]   = useState('skill');
  const [newCompId, setNewCompId] = useState('');
  const [addingMilestone, setAddingMilestone] = useState(false);
  const [busyMilestoneId, setBusyMilestoneId] = useState(null);

  const addMilestone = async () => {
    if (!newTitle.trim() || !seasonGoal?.id) return;
    setAddingMilestone(true);
    await supabase.from('milestones').insert({
      user_id: userId, season_goal_id: seasonGoal.id,
      title: newTitle.trim(), type: newType,
      competition_id: newCompId || null, status: 'active',
    });
    setNewTitle(''); setNewCompId('');
    await onRefresh();
    setAddingMilestone(false);
  };

  const setMilestoneStatus = async (m, status) => {
    setBusyMilestoneId(m.id);
    const nextStatus = m.status === status ? 'active' : status;
    await supabase.from('milestones').update({
      status: nextStatus,
      achieved_at: nextStatus === 'achieved' ? new Date().toISOString() : null,
    }).eq('id', m.id);
    await onRefresh();
    setBusyMilestoneId(null);
  };

  const setMilestoneRating = async (m, rating) => {
    setBusyMilestoneId(m.id);
    await supabase.from('milestones').update({ rating: m.rating === rating ? null : rating }).eq('id', m.id);
    await onRefresh();
    setBusyMilestoneId(null);
  };

  const deleteMilestone = async (m) => {
    setBusyMilestoneId(m.id);
    await supabase.from('milestones').delete().eq('id', m.id);
    await onRefresh();
    setBusyMilestoneId(null);
  };

  const inp = {
    width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.09)',
    border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 12px',
    color: C.text, fontSize: 13, fontFamily: C.font, outline: 'none',
  };
  const lbl = { display: 'block', color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 };

  useEscapeToClose(onClose);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.80)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#161b22', border: '1.5px solid rgba(56,189,248,0.65)',
        borderRadius: 16, padding: 24, width: '100%', maxWidth: 460,
        maxHeight: '85vh', overflowY: 'auto', fontFamily: C.font,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>🏆 Season</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '9px 12px', color: '#fca5a5', fontSize: 12.5, marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <span style={lbl}>Season name</span>
            <input value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Fall 2026"
              style={inp} autoFocus />
          </div>
          <div>
            <span style={lbl}>What does this season consist of? (optional)</span>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="e.g. 2 indoor leagues + 3 open tournaments before Nationals"
              style={{ ...inp, minHeight: 48, resize: 'vertical', fontFamily: C.font }} />
          </div>
          <div>
            <span style={lbl}>Goal</span>
            <textarea value={goalText} onChange={e => setGoalText(e.target.value)}
              placeholder="e.g. Win 4 matches and reach division 4"
              style={{ ...inp, minHeight: 64, resize: 'vertical', fontFamily: C.font }} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <span style={lbl}>Start date (optional)</span>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ ...inp, colorScheme: 'dark' }} />
            </div>
            <div style={{ flex: 1 }}>
              <span style={lbl}>End date (optional)</span>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ ...inp, colorScheme: 'dark' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={saveGoal} disabled={saving} style={{
              flex: 1, background: C.accent, border: 'none', borderRadius: 8, padding: '11px 0',
              color: '#04150f', fontWeight: 700, fontSize: 13.5, cursor: saving ? 'default' : 'pointer',
              opacity: saving ? 0.7 : 1, fontFamily: C.font,
            }}>{saving ? 'Saving…' : (seasonGoal?.id ? 'Save changes' : 'Set season goal')}</button>
            {seasonGoal?.id && (
              <button onClick={archiveGoal} title="Archive this season goal" style={{
                background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '11px 14px',
                color: C.muted, fontSize: 12.5, cursor: 'pointer', fontFamily: C.font,
              }}>Archive</button>
            )}
          </div>
        </div>

        {seasonGoal?.id && (
          <div style={{ marginTop: 24, paddingTop: 18, borderTop: `1px solid ${C.border}` }}>
            <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
              Competitions in this season
            </div>
            {competitions.length === 0 ? (
              <div style={{ color: C.dim, fontSize: 12.5, fontStyle: 'italic', marginBottom: 18 }}>
                No competitions yet — add a league or tournament first, then tag it here.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
                {competitions.filter(c => c.status !== 'archived').map(c => {
                  const linked = c.season_goal_id === seasonGoal.id;
                  const busy = linkingCompId === c.id;
                  return (
                    <button key={c.id} onClick={() => toggleCompetitionInSeason(c)} disabled={busy} style={{
                      display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
                      background: linked ? 'rgba(16,185,129,0.12)' : 'rgba(255,255,255,0.04)',
                      // Fiery orange-red for unselected rows, same treatment
                      // as every other item-card-in-a-modal — user feedback
                      // 2026-10-02 ("lisätään kaikkien modaalien kaikkiin
                      // taulukoihin").
                      border: `1.5px solid ${linked ? C.accent : 'rgba(249,115,22,0.75)'}`, borderRadius: 8,
                      padding: '7px 10px', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
                      fontFamily: C.font, width: '100%', boxSizing: 'border-box',
                    }}>
                      <span style={{ fontSize: 13 }}>{linked ? '✅' : '➕'}</span>
                      <span style={{ color: linked ? C.accent : C.text, fontSize: 12.5, fontWeight: 600 }}>
                        {competitionIcon(c.type)} {c.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
              Milestones
            </div>

            {milestones.length === 0 && (
              <div style={{ color: C.dim, fontSize: 12.5, fontStyle: 'italic', marginBottom: 12 }}>
                Break the season goal into concrete milestones — a placement target, a skill to improve, whatever matters right now.
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
              {milestones.map(m => {
                const busy = busyMilestoneId === m.id;
                const comp = competitions.find(c => c.id === m.competition_id);
                return (
                  <div key={m.id} style={{
                    background: 'rgba(255,255,255,0.04)', border: '1.5px solid rgba(249,115,22,0.75)', borderRadius: 10,
                    padding: '9px 11px', opacity: busy ? 0.5 : 1,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: C.text, fontSize: 12.5, fontWeight: 600 }}>
                          {m.type === 'skill' ? '🎯' : m.type === 'result' ? '🏆' : '📌'} {m.title}
                        </div>
                        {comp && <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>{competitionIcon(comp.type)} {comp.name}</div>}
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        <button onClick={() => setMilestoneStatus(m, 'achieved')} disabled={busy} title="Mark achieved" style={{
                          background: m.status === 'achieved' ? 'rgba(34,197,94,0.18)' : 'none',
                          border: `1px solid ${m.status === 'achieved' ? '#22c55e' : C.border}`, borderRadius: 6,
                          color: m.status === 'achieved' ? '#22c55e' : C.muted,
                          fontSize: 11.5, padding: '4px 8px', cursor: busy ? 'default' : 'pointer', fontFamily: C.font,
                        }}>✓</button>
                        <button onClick={() => setMilestoneStatus(m, 'not_achieved')} disabled={busy} title="Mark not achieved" style={{
                          background: m.status === 'not_achieved' ? 'rgba(239,68,68,0.18)' : 'none',
                          border: `1px solid ${m.status === 'not_achieved' ? '#ef4444' : C.border}`, borderRadius: 6,
                          color: m.status === 'not_achieved' ? '#ef4444' : C.muted,
                          fontSize: 11.5, padding: '4px 8px', cursor: busy ? 'default' : 'pointer', fontFamily: C.font,
                        }}>✗</button>
                        <button onClick={() => deleteMilestone(m)} disabled={busy} title="Delete" style={{
                          background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, color: C.dim,
                          fontSize: 11.5, padding: '4px 8px', cursor: busy ? 'default' : 'pointer', fontFamily: C.font,
                        }}>🗑</button>
                      </div>
                    </div>
                    {m.status === 'achieved' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                        <span style={{ color: C.muted, fontSize: 10.5 }}>How well?</span>
                        <StarRating value={m.rating || 0} onChange={v => setMilestoneRating(m, v)} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px dashed ${C.border}`, borderRadius: 10, padding: 12 }}>
              <input value={newTitle} onChange={e => setNewTitle(e.target.value)}
                placeholder="e.g. Improve first-serve consistency"
                style={{ ...inp, marginBottom: 8 }} />
              <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                {MILESTONE_TYPES.map(t => (
                  <button key={t.value} onClick={() => setNewType(t.value)} title={t.hint} style={{
                    background: newType === t.value ? 'rgba(16,185,129,0.18)' : 'rgba(255,255,255,0.09)',
                    border: `1px solid ${newType === t.value ? C.accent : C.border}`,
                    color: newType === t.value ? C.accent : C.text,
                    borderRadius: 8, padding: '6px 11px', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', fontFamily: C.font,
                  }}>{t.label}</button>
                ))}
              </div>
              {competitions.length > 0 && (
                <select value={newCompId} onChange={e => setNewCompId(e.target.value)} style={{ ...inp, marginBottom: 8 }}>
                  <option value="">Not linked to a competition</option>
                  {competitions.filter(c => c.status !== 'archived').map(c => (
                    <option key={c.id} value={c.id}>{competitionIcon(c.type)} {c.name}</option>
                  ))}
                </select>
              )}
              <button onClick={addMilestone} disabled={addingMilestone || !newTitle.trim()} style={{
                width: '100%', background: C.accent, border: 'none', borderRadius: 8, padding: '9px 0',
                color: '#04150f', fontWeight: 700, fontSize: 12.5,
                cursor: (addingMilestone || !newTitle.trim()) ? 'default' : 'pointer',
                opacity: (addingMilestone || !newTitle.trim()) ? 0.6 : 1, fontFamily: C.font,
              }}>{addingMilestone ? 'Adding…' : '+ Add milestone'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CompetitionManagerModal({ userId, competitions, plans = [], activeSport, authSession, onRefresh, onClose, initialEditing = null }) {
  // Clicking a competition row (vs. the generic "Manage →" button) should
  // land straight in that competition's edit form, not the list first —
  // see docs_zenanalytics/14-NAVIGATION-SHORTCUTS-2026-10-01.md.
  const [editing, setEditing] = useState(initialEditing); // null | 'new' | competition object
  const [tab, setTab] = useState('active'); // 'active' | 'archived'
  const [archivingId, setArchivingId] = useState(null); // brief in-flight guard, prevents double-clicks
  const [linkingId, setLinkingId] = useState(null);

  // The active season (if any) for this sport, loaded here so a competition
  // can be linked to it directly from this modal -- without this, a visitor
  // can create competitions that never attach to the season shown on Home,
  // which left "No active season set" on screen even with one genuinely
  // active (found 2026-11-01: real account had an active season but every
  // competition's season_goal_id was still null).
  const [seasonGoal, setSeasonGoal] = useState(null);
  useEffect(() => {
    if (!userId) return;
    let sq = supabase.from('season_goals').select('id,title,sport').eq('user_id', userId).eq('status', 'active');
    if (activeSport) sq = sq.eq('sport', activeSport);
    let cancelled = false;
    sq.order('created_at', { ascending: false }).limit(1).then(({ data }) => {
      if (!cancelled) setSeasonGoal((data || [])[0] || null);
    });
    return () => { cancelled = true; };
  }, [userId, activeSport]);

  const toggleSeasonLink = async (c) => {
    if (!seasonGoal?.id || linkingId) return;
    setLinkingId(c.id);
    const nextSeasonId = c.season_goal_id === seasonGoal.id ? null : seasonGoal.id;
    await supabase.from('competitions').update({ season_goal_id: nextSeasonId }).eq('id', c.id);
    await onRefresh();
    setLinkingId(null);
  };

  useEscapeToClose(onClose);

  const toggleArchive = async (c) => {
    if (archivingId) return; // one at a time — no accidental double-archive while a request is in flight
    setArchivingId(c.id);
    const nextStatus = c.status === 'archived' ? 'active' : 'archived';
    await supabase.from('competitions').update({ status: nextStatus }).eq('id', c.id);
    await onRefresh();
    setArchivingId(null);
  };

  // Duplicate — leagues/tournaments recur season to season, so starting
  // next season from last season's shape (type, goal, format_meta) beats a
  // blank form. Dates are left blank since they're the one thing that
  // never carries over. See
  // docs_zenanalytics/09-CREATE-AND-MANAGE-REDESIGN-2026-09-29.md §3.4.
  const duplicateCompetition = async (c) => {
    if (archivingId) return;
    setArchivingId(c.id);
    // reflection is specifically about how *that* season went — doesn't
    // belong carried into a new one, unlike strategy/bracket shape which do.
    const { reflection, ...formatMetaToCopy } = c.format_meta || {};
    const { data } = await supabase.from('competitions').insert({
      user_id: userId,
      sport: c.sport,
      name: `${c.name} (copy)`,
      type: c.type,
      goal: c.goal || null,
      format_meta: Object.keys(formatMetaToCopy).length > 0 ? formatMetaToCopy : null,
      start_date: null,
      end_date: null,
      status: 'active',
    }).select().single();
    await onRefresh();
    setArchivingId(null);
    if (data) setEditing(data); // straight into edit, ready to set dates
  };

  const active   = competitions.filter(c => c.status !== 'archived');
  const archived = competitions.filter(c => c.status === 'archived');

  const fmtRange = (c) => {
    if (!c.start_date && !c.end_date) return null;
    const f = (d) => d ? formatDateLocaleAware(d) : '?';
    return `${f(c.start_date)} – ${f(c.end_date)}`;
  };

  const recordFor = (c) => {
    const matches = plans.filter(p => p.competition_id === c.id && ['match', 'competition'].includes(p.type) && p.result);
    if (!matches.length) return null;
    const w = matches.filter(p => p.result === 'win').length;
    const d = matches.filter(p => p.result === 'draw').length;
    const l = matches.filter(p => p.result === 'loss').length;
    return d > 0 ? `${w}W-${d}D-${l}L` : `${w}W-${l}L`;
  };

  const Row = ({ c }) => {
    const busy = archivingId === c.id;
    const linking = linkingId === c.id;
    const linked = !!seasonGoal && c.season_goal_id === seasonGoal.id;
    const btn = (extra) => ({
      background: 'none', border: `1px solid ${C.border}`, borderRadius: 7, color: C.muted,
      fontSize: 12, padding: '7px 6px', cursor: busy ? 'default' : 'pointer', fontFamily: C.font,
      width: '100%', textAlign: 'center', ...extra,
    });
    return (
      <div style={{
        // Same cool blue as the modal's own border — a consistent palette
        // instead of the clashing orange cards flagged 2026-11-01.
        background: 'rgba(255,255,255,0.04)', border: `1.5px solid ${C.border}`, borderRadius: 10,
        padding: '12px', marginBottom: 8, opacity: busy ? 0.4 : 1, transition: 'opacity 0.15s',
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: C.text, fontSize: 13.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {competitionIcon(c.type)} {c.name}
            {c.goal_status === 'achieved' && (
              <span style={{ color: '#22c55e', fontSize: 10.5, fontWeight: 700, border: '1px solid #22c55e', borderRadius: 20, padding: '1px 7px' }}>✓ ACHIEVED</span>
            )}
            {c.goal_status === 'not_achieved' && (
              <span style={{ color: '#ef4444', fontSize: 10.5, fontWeight: 700, border: '1px solid #ef4444', borderRadius: 20, padding: '1px 7px' }}>✗ NOT ACHIEVED</span>
            )}
          </div>
          <div style={{ color: C.muted, fontSize: 11.5, marginTop: 2 }}>
            {c.type}{fmtRange(c) ? ` · ${fmtRange(c)}` : ''}{c.location ? ` · ${c.location}` : ''}
            {recordFor(c) && <span style={{ color: C.text, fontWeight: 700 }}> · {recordFor(c)}</span>}
          </div>
          {c.goal && (
            <div style={{ color: C.text, fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>
              🎯 {c.goal}
            </div>
          )}
        </div>

        {/* Season link — the control that was missing entirely before
            2026-11-01: without it, a competition could never show up under
            its season on Home no matter how "active" the season was. */}
        {seasonGoal && (
          <button onClick={() => toggleSeasonLink(c)} disabled={busy || linking} title={linked ? `Linked to ${seasonGoal.title || 'current season'} — tap to unlink` : `Link to ${seasonGoal.title || 'current season'}`} style={{
            display: 'flex', alignItems: 'center', gap: 6, width: '100%', marginTop: 10,
            background: linked ? 'rgba(234,179,8,0.14)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${linked ? '#eab308' : C.border}`, borderRadius: 7,
            color: linked ? '#eab308' : C.muted, fontSize: 11.5, fontWeight: 700,
            padding: '7px 9px', cursor: (busy || linking) ? 'default' : 'pointer', fontFamily: C.font,
          }}>
            <span>{linked ? '🏆' : '○'}</span>
            <span>{linking ? '…' : linked ? `Linked to ${seasonGoal.title || 'current season'}` : `Link to ${seasonGoal.title || 'current season'}`}</span>
          </button>
        )}

        {/* Evaluating the goal (Met/Missed) belongs on the competition's own
            tab, where preparation and reflection actually happen -- this
            list is for quick management only, per feedback 2026-11-01. The
            ACHIEVED/NOT ACHIEVED badge above still reflects whatever was set
            there. */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 6, marginTop: 10,
        }}>
          <button onClick={() => setEditing(c)} disabled={busy} title="Edit" style={btn()}>Edit</button>
          <button onClick={() => duplicateCompetition(c)} disabled={busy} title="Copy as template for next season" style={btn()}>Copy</button>
          <button onClick={() => toggleArchive(c)} disabled={busy} title={c.status === 'archived' ? 'Unarchive' : 'Archive'} style={btn()}>
            {busy ? '…' : (c.status === 'archived' ? 'Unarchive' : 'Archive')}
          </button>
        </div>
      </div>
    );
  };

  if (editing) {
    return (
      <CompetitionFormModal
        competition={editing === 'new' ? null : editing}
        userId={userId}
        activeSport={activeSport}
        authSession={authSession}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); onRefresh(); }}
      />
    );
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.80)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#161b22', border: '1.5px solid rgba(56,189,248,0.65)',
        borderRadius: 16, padding: 24, width: '100%', maxWidth: 460,
        maxHeight: '82vh', overflowY: 'auto', fontFamily: C.font,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>🏆 Competitions</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ color: C.muted, fontSize: 12, marginBottom: 16 }}>
          Leagues, tournaments, and practice goals your Game Plans can belong to.
        </div>

        <button onClick={() => setEditing('new')} style={{
          width: '100%', background: 'rgba(16,185,129,0.14)', border: `1px solid ${C.accent}60`,
          borderRadius: 10, padding: '10px 0', color: C.accent, fontWeight: 700, fontSize: 13,
          cursor: 'pointer', fontFamily: C.font, marginBottom: 16,
        }}>+ New competition</button>

        {/* Active / Archived tabs — a deliberate switch, not an implicit reflow,
            so archiving one competition can't be mistaken for another moving
            or disappearing. */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
          {[
            { key: 'active',   label: `Active (${active.length})` },
            { key: 'archived', label: `Archived (${archived.length})` },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '6px 14px', borderRadius: 8, fontWeight: 600, fontSize: 12.5,
              fontFamily: C.font, cursor: 'pointer',
              background: tab === t.key ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${tab === t.key ? `${C.accent}60` : C.border}`,
              color: tab === t.key ? C.accent : C.muted,
            }}>{t.label}</button>
          ))}
        </div>

        {tab === 'active' ? (
          active.length === 0 ? (
            <div style={{ color: C.dim, fontSize: 12.5, fontStyle: 'italic', textAlign: 'center', padding: '12px 0' }}>
              No active competitions.
            </div>
          ) : active.map(c => <Row key={c.id} c={c} />)
        ) : (
          archived.length === 0 ? (
            <div style={{ color: C.dim, fontSize: 12.5, fontStyle: 'italic', textAlign: 'center', padding: '12px 0' }}>
              No archived competitions yet.
            </div>
          ) : archived.map(c => <Row key={c.id} c={c} />)
        )}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function GamePlanTab({ session, activeSport, onNavigateToAnalysis, pendingOpenPlan, onPendingOpenPlanConsumed, pendingOpenCompetition, onPendingOpenCompetitionConsumed, readOnly = false, closeModalTrigger = 0, demoMode = false }) {
  const userId   = session?.user?.id;
  const today    = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const isMobile = useIsMobile();

  const [showUnallocated, setShowUnallocated] = useState(false);
  const [plans,        setPlans]        = useState([]);
  // Full isolation between sports (2026-10-18, PRODUCT-GUIDELINES.md §5) —
  // `plans` keeps every sport (mutations/refresh logic below don't change),
  // but everything rendered reads this filtered view instead.
  const visiblePlans = useSportFiltered(plans, activeSport);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState(null); // null = no error, string = error message
  const [modal,        setModal]        = useState(null);
  const [gamePlanSports, setGamePlanSports] = useState([]);
  const [seasonGoal,   setSeasonGoal]   = useState(null); // active season_goals row, or null
  const [milestones,   setMilestones]   = useState([]);
  const [showSeasonGoalManager, setShowSeasonGoalManager] = useState(false);
  const [competitions, setCompetitions] = useState([]);
  const [showCompManager, setShowCompManager] = useState(false);
  const [compStatusFilter, setCompStatusFilter] = useState('active'); // 'active' | 'all' | 'archived'

  // activeSportRef: guards against a stale-response race when activeSport
  // changes while a request is still in flight — a slower response for the
  // previous sport could otherwise overwrite the correct, faster one for
  // the new sport. Found 2026-10-18 testing all 6 sports (a stale season
  // goal briefly leaked across sports on Home); same risk applies here.
  // See PRODUCT-GUIDELINES.md §5.
  const activeSportRef = useRef(activeSport);
  useEffect(() => { activeSportRef.current = activeSport; }, [activeSport]);

  const loadCompetitions = useCallback(() => {
    if (!userId) return;
    const forSport = activeSport;
    let q = supabase.from('competitions').select('*').eq('user_id', userId);
    if (activeSport) q = q.eq('sport', activeSport);
    q.order('start_date', { ascending: false }).then(({ data }) => {
      if (activeSportRef.current !== forSport) return;
      setCompetitions(data || []);
    });
  }, [userId, activeSport]);

  useEffect(() => { loadCompetitions(); }, [loadCompetitions]);

  // Season goal + its milestones — see
  // docs_zenanalytics/10-SEASON-GOALS-MILESTONES-2026-09-29.md. Replaces the
  // old free-text user_profiles.goals field with a structured, timeboxed
  // goal + a milestone hierarchy underneath it.
  const loadSeasonGoal = useCallback(() => {
    const forSport = activeSport;
    if (demoMode) {
      fetch(`${API}/game-plans/demo/season-goal`)
        .then(r => r.json())
        .then(d => { setSeasonGoal(d.season_goal || null); setMilestones(d.milestones || []); })
        .catch(e => console.error('[GamePlanTab] demo season-goal load failed:', e));
      return;
    }
    if (!userId) return;
    let sq = supabase.from('season_goals').select('*').eq('user_id', userId).eq('status', 'active');
    if (activeSport) sq = sq.eq('sport', activeSport);
    sq.order('created_at', { ascending: false }).limit(1).then(({ data }) => {
        if (activeSportRef.current !== forSport) return;
        const sg = (data || [])[0] || null;
        setSeasonGoal(sg);
        if (sg) {
          supabase.from('milestones').select('*').eq('season_goal_id', sg.id)
            .order('created_at').then(({ data: ms }) => {
              if (activeSportRef.current !== forSport) return;
              setMilestones(ms || []);
            });
        } else {
          setMilestones([]);
        }
      });
  }, [userId, demoMode, activeSport]);

  useEffect(() => { loadSeasonGoal(); }, [loadSeasonGoal]);

  // Close modal when tutorial navigates away from session steps
  useEffect(() => {
    if (closeModalTrigger > 0) setModal(null);
  }, [closeModalTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open SessionModal when App.js passes a plan from the auto-match popup or tutorial
  useEffect(() => {
    if (pendingOpenPlan) {
      setModal({ date: pendingOpenPlan.scheduled_date?.slice(0,10), session: pendingOpenPlan, initialTab: pendingOpenPlan._initialTab });
      if (onPendingOpenPlanConsumed) onPendingOpenPlanConsumed();
    }
  }, [pendingOpenPlan]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open a competition's edit form when Home's Season module is
  // clicked — same cross-tab deep-link pattern as pendingOpenPlan above.
  // See docs_zenanalytics/14-NAVIGATION-SHORTCUTS-2026-10-01.md.
  useEffect(() => {
    if (pendingOpenCompetition) {
      setShowCompManager(pendingOpenCompetition);
      if (onPendingOpenCompetitionConsumed) onPendingOpenCompetitionConsumed();
    }
  }, [pendingOpenCompetition]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load user's Game Plan sport filter
  useEffect(() => {
    if (!userId) return;
    fetch(`${API}/profiles/${userId}/player-profile`, {
      headers: { ...(session?.access_token && { Authorization: `Bearer ${session.access_token}` }) },
    })
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        if (data?.game_plan_sports) {
          setGamePlanSports(data.game_plan_sports.split(',').filter(Boolean));
        }
      })
      .catch(e => console.error('[GamePlanTab] sport filter load failed:', e));
  }, [userId]);

  const loadPlans = useCallback(async () => {
    if (demoMode) {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await fetch(`${API}/game-plans/demo`);
        const data = res.ok ? await res.json() : { plans: [] };
        setPlans(data.plans || []);
      } catch (e) {
        setLoadError('Network error');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    setLoadError(null);
    try {
      const { data, error } = await fetchAllPlans(userId);
      if (error) {
        console.error('[loadPlans] Supabase error:', error);
        setLoadError(error.message || error.code || 'Unknown error');
      } else {
        setPlans(data || []);
      }
    } catch (e) {
      console.error('[loadPlans] exception:', e);
      setLoadError(e?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [userId, demoMode]);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const openAdd        = (date) => { if (!readOnly) setModal({ date: toISO(date), session: null, prefillSport: gamePlanSports[0] || undefined }); };
  const openEdit       = (s)    => setModal({ date: s.scheduled_date?.slice(0,10), session: s });
  const openEditResult = (s)    => setModal({ date: s.scheduled_date?.slice(0,10), session: s, initialTab: 'result' });

  const handleSaved = () => { setModal(null); loadPlans(); };

  const todayISO = toISO(today);

  // "Right now" — sessions scheduled today or earlier with no result logged
  // yet. Same "needs attention" idea the Scheduled stat already counts,
  // just surfaced as its own line instead of buried in a number. Renders
  // nothing when empty — no manufactured content. See
  // docs_zenanalytics/09-CREATE-AND-MANAGE-REDESIGN-2026-09-29.md §3.2.
  const needsResult = useMemo(() =>
    visiblePlans
      .filter(p => p.scheduled_date?.slice(0, 10) <= todayISO && !p.result && !p.performance_score)
      .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date)),
    [visiblePlans, todayISO]
  );

  return (
    <div style={{
      minHeight: '100vh', fontFamily: C.font, background: C.bg,
      position: 'relative', paddingBottom: 80,
    }}>
      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1, padding: '16px 16px 0', maxWidth: 720, margin: '0 auto' }}>
        {/* Header — just the page title. "Recently deleted" moved out of
            here (it read as an odd, out-of-place action next to the
            title) into a small link inside the Create new game plan
            module, next to "See past game plans" — same treatment. */}
        <div style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0, color: C.text, fontSize: 18, fontWeight: 700 }}>🪄 Create & Manage</h2>
        </div>

        {/* Section order (2026-10-28 restructure): Matches -> Competitions
            -> Season -> Equipment, mirroring Home's own order and its
            rationale — daily usage frequency ("+ Add game plan" / logging a
            result is the single most common action here too), not top-down
            hierarchy. See
            docs_zenanalytics/25-STRUCTURE-AND-CAREER-RECORD-PLANNING-2026-10-28.md. */}

        <LayerSection id="cm-matches" userId={userId} icon="🎾" title="Matches" rightSlot={
          <button onClick={() => openAdd(today)} disabled={readOnly} style={{
            background: readOnly ? 'rgba(16,185,129,0.25)' : C.accent,
            border: 'none', color: readOnly ? 'rgba(255,255,255,0.4)' : '#fff',
            borderRadius: 20, padding: '6px 16px', fontSize: 13, fontWeight: 700,
            cursor: readOnly ? 'default' : 'pointer', fontFamily: C.font,
          }}>+ Add game plan</button>
        }>
          {/* "Waiting for result" — only renders when something actually
              needs attention. Same list Home's What's Next card now also
              surfaces (user feedback 2026-10-28) — kept here too since this
              is still the view edits actually happen from. */}
          {needsResult.length > 0 && (
            <div style={{
              background: 'rgba(245,158,11,0.08)', border: `1px solid ${C.amber}40`,
              borderRadius: 10, padding: '9px 14px', marginBottom: 14,
            }}>
              <div style={{ color: C.amber, fontSize: 12.5, fontWeight: 700 }}>
                ⏳ {needsResult.length} session{needsResult.length !== 1 ? 's' : ''} need{needsResult.length === 1 ? 's' : ''} a result
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: needsResult.length ? 6 : 0 }}>
                {needsResult.slice(0, 3).map(p => (
                  <div key={p.id} onClick={() => openEditResult(p)} role="button" tabIndex={0} onKeyDown={onEnterOrSpace(() => openEditResult(p))} style={{
                    color: C.text, fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline',
                    textDecorationColor: 'rgba(245,158,11,0.35)',
                  }}>
                    {fmtDate(p.scheduled_date?.slice(0, 10))} · {sportEmoji(p.sport)} {p.goal || sportLabel(p.sport)}
                  </div>
                ))}
                {needsResult.length > 3 && (
                  <div style={{ color: C.muted, fontSize: 11.5 }}>+{needsResult.length - 3} more below</div>
                )}
              </div>
            </div>
          )}

          {/* Create new game plan — the actual creation entry point
              ("+ Add game plan" lives in this section's header now), plus a
              compact live summary. Past game plans are hidden by default to
              keep this module tight (they're one tap away, and always on
              Home too) — see docs_zenanalytics/11-MODULE-CENTRIC-REDESIGN-2026-09-29.md.
              The StatsStrip 30-day tile that used to render here was dropped
              2026-10-28 (user feedback): it duplicated Home's own 30-day
              summary, and this is a management view, not an analytics one —
              see planning doc "keskitytään siihen, että tuo on
              hallintonäkymä". */}
          <div style={{
            background: 'rgba(15,23,42,0.82)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
            border: `1.5px solid ${C.moduleBorder}`, borderRadius: 12, padding: '10px 14px', marginBottom: 14,
          }}>
            {/* Loading indicator */}
            {loading && (
              <div style={{
                textAlign: 'center', padding: '28px 0 12px',
                color: C.muted, fontSize: 13,
              }}>
                Loading game plans…
              </div>
            )}

            {/* Load error + retry */}
            {!loading && loadError && (
              <div style={{
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                borderRadius: 10, padding: '14px 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ color: '#f87171', fontSize: 13 }}>⚠️ Failed to load game plans: {loadError}</span>
                <button onClick={loadPlans} style={{
                  background: C.accent, border: 'none', color: '#fff',
                  borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700,
                  cursor: 'pointer', fontFamily: C.font,
                }}>🔄 Retry</button>
              </div>
            )}

            {/* Session summary: upcoming only — past is a one-tap-away link, not a full table */}
            {!loading && !loadError && visiblePlans.length > 0 && (
              <SessionSummarySection
                plans={visiblePlans}
                today={today}
                onEdit={openEdit}
                onRefresh={loadPlans}
                showPast={false}
              />
            )}

            {!loading && visiblePlans.length === 0 && (
              <div style={{ textAlign: 'center', padding: '30px 20px', color: C.muted }}>
                <div style={{ fontSize: 40, marginBottom: 14 }}>🎯</div>
                <div style={{ fontWeight: 700, marginBottom: 8, color: C.text, fontSize: 16 }}>Plan your first session</div>
                <div style={{ fontSize: 13, lineHeight: 1.6 }}>Use the + Add game plan button above to get started.</div>
              </div>
            )}
          </div>
        </LayerSection>

        {/* Competitions — Tournaments/Leagues split table, shared with Home
            (CompetitionsSplitTable.jsx). See planning doc round 3. */}
        <LayerSection id="cm-competitions" userId={userId} icon="🏆" title="Competitions" rightSlot={
          <>
            <select value={compStatusFilter} onChange={e => setCompStatusFilter(e.target.value)} style={{
              background: 'rgba(255,255,255,0.09)', border: `1px solid ${C.border}`, borderRadius: 6,
              color: C.text, fontSize: 11.5, padding: '4px 8px', fontFamily: C.font, cursor: 'pointer',
            }}>
              <option value="active">Active</option>
              <option value="all">All</option>
              <option value="archived">Archived</option>
            </select>
            <button onClick={() => setShowCompManager(true)} disabled={readOnly} style={{
              background: readOnly ? 'none' : 'rgba(16,185,129,0.12)', border: `1px solid ${readOnly ? C.border : C.accent}`,
              borderRadius: 6, color: readOnly ? C.dim : C.accent, fontSize: 11.5, fontWeight: 700, padding: '5px 12px',
              cursor: readOnly ? 'default' : 'pointer', fontFamily: C.font,
            }}>Manage →</button>
          </>
        }>
          <div style={{
            background: 'rgba(15,23,42,0.82)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
            border: `1.5px solid ${C.moduleBorder}`, borderRadius: 12, padding: '10px 14px', marginBottom: 14,
          }}>
            {(() => {
              const filteredComps = competitions.filter(c =>
                compStatusFilter === 'all' ? true : compStatusFilter === 'active' ? c.status !== 'archived' : c.status === 'archived'
              );
              if (filteredComps.length === 0) {
                return (
                  <div onClick={() => !readOnly && setShowCompManager(true)} role="button" tabIndex={0} onKeyDown={onEnterOrSpace(() => !readOnly && setShowCompManager(true))} style={{
                    color: C.dim, fontSize: 13, fontStyle: 'italic', cursor: readOnly ? 'default' : 'pointer',
                  }}>
                    {competitions.length === 0 ? 'No competitions yet — add your first league, tournament or goal…' : `No ${compStatusFilter} competitions.`}
                  </div>
                );
              }
              return (
                <CompetitionsSplitTable
                  competitions={filteredComps}
                  plans={visiblePlans}
                  onRowClick={setShowCompManager}
                  canManage={!readOnly}
                />
              );
            })()}
          </div>
        </LayerSection>
        {showCompManager && (
          <CompetitionManagerModal
            userId={userId}
            competitions={competitions}
            plans={visiblePlans}
            activeSport={activeSport}
            authSession={session}
            onRefresh={loadCompetitions}
            onClose={() => setShowCompManager(false)}
            initialEditing={typeof showCompManager === 'object' ? showCompManager : null}
          />
        )}

        {/* Season — a named, bounded time period (can span several
            leagues/tournaments) with its own goal, the competitions tagged
            into it, and the milestones that break the goal down into
            concrete steps — all shown indented right here instead of only
            behind "Manage". See
            docs_zenanalytics/12-SEASON-REDESIGN-2026-10-01.md. */}
        <LayerSection id="cm-season" userId={userId} icon="🏆" title="Season" rightSlot={
          <button onClick={() => setShowSeasonGoalManager(true)} disabled={readOnly} style={{
            background: readOnly ? 'none' : 'rgba(16,185,129,0.12)', border: `1px solid ${readOnly ? C.border : C.accent}`,
            borderRadius: 6, color: readOnly ? C.dim : C.accent, fontSize: 11.5, fontWeight: 700, padding: '5px 12px',
            cursor: readOnly ? 'default' : 'pointer', fontFamily: C.font,
          }}>Manage →</button>
        }>
        <div style={{
          background: 'rgba(15,23,42,0.82)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
          border: `1.5px solid ${C.moduleBorder}`, borderRadius: 12, padding: '10px 14px', marginBottom: 14,
        }}>
          {!seasonGoal ? (
            <div onClick={() => !readOnly && setShowSeasonGoalManager(true)} role="button" tabIndex={0} onKeyDown={onEnterOrSpace(() => !readOnly && setShowSeasonGoalManager(true))} style={{
              color: C.dim, fontSize: 13, marginTop: 8, fontStyle: 'italic', cursor: readOnly ? 'default' : 'pointer',
            }}>
              Name this season, describe what it consists of, and break its goal into milestones…
            </div>
          ) : (() => {
            const seasonComps = competitions.filter(c => c.season_goal_id === seasonGoal.id);
            return (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>
                  {seasonGoal.name || <span style={{ fontStyle: 'italic', color: C.dim, fontWeight: 400 }}>Unnamed season</span>}
                </span>
                {(seasonGoal.start_date || seasonGoal.end_date) && (
                  <span style={{ color: C.muted, fontSize: 11 }}>
                    {seasonGoal.start_date ? formatDateLocaleAware(seasonGoal.start_date) : '?'}
                    {' – '}
                    {seasonGoal.end_date ? formatDateLocaleAware(seasonGoal.end_date) : '?'}
                  </span>
                )}
              </div>
              {seasonGoal.description && (
                <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.4, marginTop: 3, fontStyle: 'italic' }}>
                  {seasonGoal.description}
                </div>
              )}
              <div style={{ color: C.text, fontSize: 13, lineHeight: 1.4, marginTop: 6 }}>
                🎯 {seasonGoal.goal || <span style={{ fontStyle: 'italic', color: C.dim }}>No goal text set yet</span>}
              </div>

              {/* Linked competitions — indented sub-section */}
              {seasonComps.length > 0 && (
                <div style={{ marginTop: 10, paddingLeft: 10, borderLeft: `2px solid ${C.moduleBorder}` }}>
                  <div style={{ color: C.muted, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {seasonComps.length} competition{seasonComps.length !== 1 ? 's' : ''} in this season
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 5 }}>
                    {seasonComps.map(c => (
                      <div key={c.id} onClick={() => !readOnly && setShowCompManager(c)} role="button" tabIndex={0} onKeyDown={onEnterOrSpace(() => !readOnly && setShowCompManager(c))} style={{
                        color: C.text, fontSize: 12, fontWeight: 600,
                        cursor: readOnly ? 'default' : 'pointer',
                      }}>
                        {competitionIcon(c.type)} {c.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Milestones — indented sub-section */}
              {milestones.length > 0 && (() => {
                const ongoingCount = milestones.filter(m => m.status === 'active').length;
                const completedCount = milestones.length - ongoingCount;
                return (
                  <div style={{ marginTop: 10, paddingLeft: 10, borderLeft: `2px solid ${C.moduleBorder}` }}>
                    <div style={{ color: C.muted, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {ongoingCount} milestone{ongoingCount !== 1 ? 's' : ''} ongoing · {completedCount} completed
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 6 }}>
                      {milestones.map(m => {
                        const verdictColor = m.status === 'achieved' ? '#22c55e' : m.status === 'not_achieved' ? '#ef4444' : C.muted;
                        const verdictLabel = m.status === 'achieved' ? 'Achieved' : m.status === 'not_achieved' ? 'Not achieved' : 'Ongoing';
                        return (
                          <div key={m.id} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                            background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`, borderRadius: 8,
                            padding: '6px 10px',
                          }}>
                            <span style={{ color: C.text, fontSize: 12, fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {m.type === 'skill' ? '🎯 ' : m.type === 'result' ? '🏆 ' : '📌 '}{m.title}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                              {m.status === 'achieved' && m.rating > 0 && (
                                <span style={{ color: C.amber, fontSize: 11 }}>{'★'.repeat(m.rating)}{'☆'.repeat(5 - m.rating)}</span>
                              )}
                              <span style={{ color: verdictColor, fontSize: 10.5, fontWeight: 700 }}>{verdictLabel}</span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
            );
          })()}
        </div>
        {showSeasonGoalManager && (
          <SeasonGoalManagerModal
            userId={userId}
            seasonGoal={seasonGoal}
            milestones={milestones}
            competitions={competitions}
            activeSport={activeSport}
            onRefresh={async () => { await Promise.all([loadSeasonGoal(), loadCompetitions()]); }}
            onClose={() => setShowSeasonGoalManager(false)}
          />
        )}
        </LayerSection>

        {/* Equipment — racket, strings, shoes. Its own section (not folded
            into Season/Competitions) matching the agreed Create & Manage
            order: Matches -> Competitions -> Season -> Equipment. */}
        <LayerSection id="cm-equipment" userId={userId} icon="🎒" title="Equipment">
          <EquipmentStatModule userId={userId} activeSport={activeSport} readOnly={readOnly} demoMode={demoMode} plans={visiblePlans} />
        </LayerSection>
      </div>

      {/* Session modal */}
      {modal && (
        <SessionModal
          session={modal.session}
          date={modal.date}
          userId={userId}
          authSession={session}
          gamePlanSports={gamePlanSports}
          activeSport={activeSport}
          onSaved={handleSaved}
          onClose={() => setModal(null)}
          initialTab={modal.initialTab}
          prefillSport={modal.prefillSport}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

export { SessionModal, toISO, SeasonGoalManagerModal, CompetitionManagerModal, competitionIcon };
